import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Search, Plus, Download, Building2, Phone, UserRound, X, FileText, Trash2, RefreshCcw, CheckSquare } from 'lucide-react';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import { generatePropertyInquiryLetter } from './letterGenerator';
import authFetch from '../../utils/authFetch';
import { resolveContactConflictResponse } from '../../utils/contactResolution';
import UniversalChecklistModal from '../../components/UniversalChecklistModal';
import MasterParcelChecklistSection from '../../components/MasterParcelChecklistSection';

// API Base URL detection
const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';

// Helper to format dates for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  
  // If already in DD.MM.YYYY format, return as is
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateString)) {
    return dateString;
  }
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString || 'N/A';
  
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}.${month}.${year}`;
};

// Attempt to extract KO name from ZK `cadastral_details`
const extractKoNameFromDetails = (cadastralDetails = '') => {
  const match = cadastralDetails.match(/katastrska\s+občina\s+\d+\s+([^\n,()]*?)(?=\s*(?:ID|številka|parcel|parcele|stavbe|$))/i);
  return match ? match[1].trim() : '';
};

const parseZkDateForCompare = (value) => {
  if (!value) return null;
  const stringValue = String(value).trim();

  // Handle formats like DD.MM.YYYY or DD.MM.YYYY - HH:MM:SS
  const match = stringValue.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (match) {
    const [, d, m, y] = match;
    const parsed = new Date(Number(y), Number(m) - 1, Number(d));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  const date = new Date(stringValue);
  return isNaN(date.getTime()) ? null : date;
};

// Check if ZK date is older than 14 days
const isZkDateStale = (zkDate) => {
  const parsed = parseZkDateForCompare(zkDate);
  if (!parsed) return false;
  const now = new Date();
  const diffMs = now.getTime() - parsed.getTime();
  const fourteenDaysMs = 14 * 24 * 60 * 60 * 1000;
  return diffMs > fourteenDaysMs;
};

const normalizeKeyPart = (value) => String(value || '').trim().toLowerCase();

const buildPropertyKey = (ko, id) => `${normalizeKeyPart(ko)}|${normalizeKeyPart(id)}`;

const normalizeOwnerName = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

const normalizeComparableLeadGenText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const isUnbuiltLandType = (value) => {
  const normalized = normalizeComparableLeadGenText(value);
  return normalized === 'nepozidano stavbno zemljišče'
    || normalized === 'nepozidana zazidljiva parcela';
};

const isHouseType = (value) => normalizeComparableLeadGenText(value) === 'hiša';

const supportsPastSales = (value) => isUnbuiltLandType(value) || isHouseType(value);

const DEFAULT_PROPERTY_TYPES = [
  'Nepozidana zazidljiva parcela',
  'Stanovanje',
  'Hiša'
];

const DEFAULT_COLD_CALLING_TYPES = [
  'stanovanje',
  'hiša',
  'vikend',
  'posest',
  'poslovni prostor',
  'garaža',
  'počitniški objekt'
];

const PROPERTY_TYPES_STORAGE_KEY = 'leadGenPropertyTypes';

const loadStoredTypes = (storageKey, fallback) => {
  if (typeof window === 'undefined') return fallback;

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return fallback;

    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return fallback;

    const values = parsed
      .map((entry) => String(entry || '').trim())
      .filter((entry) => entry && entry !== 'MPT');
    if (values.length === 0) {
      return fallback;
    }

    const fallbackValues = Array.isArray(fallback)
      ? fallback.map((entry) => String(entry || '').trim()).filter(Boolean)
      : [];

    return [...values, ...fallbackValues.filter((entry) => !values.includes(entry))];
  } catch (error) {
    return fallback;
  }
};

const persistStoredTypes = (storageKey, values) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(storageKey, JSON.stringify(values));
};

const DEFAULT_STATUS_COLORS = {
  'Novo': '#3b82f6',
  'Enquiry sent': '#f59e0b',
  'Closed': '#64748b'
};

const normalizeStatusColorValue = (value, fallbackColor = '#64748b') => {
  const normalizedValue = String(value || '').trim();
  return /^#[0-9a-fA-F]{6}$/.test(normalizedValue) ? normalizedValue.toLowerCase() : fallbackColor;
};

const getDefaultStatusColor = (statusName) => DEFAULT_STATUS_COLORS[String(statusName || '').trim()] || '#64748b';

const normalizeStatusOption = (option) => {
  if (typeof option === 'string') {
    return {
      name: option,
      color: getDefaultStatusColor(option)
    };
  }

  const name = String(option?.name || option?.statusName || '').trim();
  return {
    name,
    color: normalizeStatusColorValue(option?.color || option?.colorHex, getDefaultStatusColor(name))
  };
};

const getStatusOptionName = (option) => normalizeStatusOption(option).name;
const getStatusOptionColor = (option) => normalizeStatusOption(option).color;

const LAST_PROPERTY_KO_STORAGE_KEY = 'leadGenLastPropertyKo';
const LAST_PROPERTY_KO_MAX_AGE_MS = 5 * 60 * 1000;

const getRecentLeadGenKo = () => {
  if (typeof window === 'undefined') return '';

  try {
    const rawValue = window.localStorage.getItem(LAST_PROPERTY_KO_STORAGE_KEY);
    if (!rawValue) return '';

    const parsedValue = JSON.parse(rawValue);
    const ko = String(parsedValue?.ko || '').trim();
    const timestamp = Number(parsedValue?.timestamp || 0);

    if (!ko || !Number.isFinite(timestamp) || (Date.now() - timestamp) > LAST_PROPERTY_KO_MAX_AGE_MS) {
      window.localStorage.removeItem(LAST_PROPERTY_KO_STORAGE_KEY);
      return '';
    }

    return ko;
  } catch (error) {
    window.localStorage.removeItem(LAST_PROPERTY_KO_STORAGE_KEY);
    return '';
  }
};

const rememberLeadGenKo = (ko) => {
  if (typeof window === 'undefined') return;

  const trimmedKo = String(ko || '').trim();
  if (!trimmedKo) return;

  window.localStorage.setItem(LAST_PROPERTY_KO_STORAGE_KEY, JSON.stringify({
    ko: trimmedKo,
    timestamp: Date.now()
  }));
};

const LAST_LEAD_TYPE_KEY = 'leadGenLastPropertyType';

const getLastLeadType = () => {
  try { return localStorage.getItem(LAST_LEAD_TYPE_KEY) || 'Nepozidana zazidljiva parcela'; } catch (e) { return 'Nepozidana zazidljiva parcela'; }
};

const rememberLeadType = (type) => {
  try { if (type) localStorage.setItem(LAST_LEAD_TYPE_KEY, type); } catch (e) {}
};

const createDefaultNewProperty = () => ({
  katastrskaObcina: getRecentLeadGenKo(),
  idParceleStavbe: '',
  idStavbe: '',
  naslov: '',
  type: getLastLeadType(),
  status: 'Novo',
  customStatus: ''
});

const createDefaultNewColdCall = () => ({
  type: DEFAULT_COLD_CALLING_TYPES[0],
  phone: '',
  listingLink: '',
  name: '',
  katastrskaObcina: '',
  idParceleStavbe: '',
  kraj: '',
  idStavbe: '',
  naslov: '',
  ownerNames: '',
  owners: [],
  zkDate: '',
  zkExtractId: null,
  email: '',
  notes: '',
  status: 'Novo',
  customStatus: '',
  lastContact: ''
});

const CHECKLIST_SCOPE = 'lead_gen_property';
const CHECKLIST_ENTITY_TYPE = 'property_lead';
const COLD_CALL_CHECKLIST_SCOPE = 'lead_gen_cold_call';
const COLD_CALL_CHECKLIST_ENTITY_TYPE = 'cold_calling_lead';

const normalizeChecklistDocumentValue = (value) => {
  const documentName = String(value?.documentName || '').trim();
  const documentPath = String(value?.documentPath || '').trim();
  if (!documentName || !documentPath) {
    return null;
  }

  return { documentName, documentPath };
};

const getChecklistDocumentValues = (value) => (Array.isArray(value) ? value : [value])
  .map((documentValue) => normalizeChecklistDocumentValue(documentValue))
  .filter(Boolean);

const isChecklistValueFilled = (item, value) => {
  if (!item) return false;
  if (item.type === 'checkbox') return value === true;
  if (item.type === 'document') return getChecklistDocumentValues(value).length > 0;
  return String(value ?? '').trim() !== '';
};

const formatChecklistValue = (item, value) => {
  if (item?.type === 'checkbox') {
    return value === true ? 'checked' : '';
  }

  if (item?.type === 'document') {
    return getChecklistDocumentValues(value).map((documentValue) => documentValue.documentName).join(', ');
  }

  return String(value ?? '').trim();
};

const formatDisplayDate = (value) => {
  if (!value) return 'N/A';
  return formatDateForDisplay(value);
};

const formatDateForInput = (value) => {
  if (!value) return '';
  const stringValue = String(value).trim();
  const isoMatch = stringValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const slMatch = stringValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (slMatch) {
    return `${slMatch[3]}-${slMatch[2]}-${slMatch[1]}`;
  }

  const parsed = new Date(stringValue);
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

const formatDisplayPrice = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return String(value);
  return `${Math.round(numericValue).toLocaleString('sl-SI')} €`;
};

const transformPropertyLead = (lead) => ({
  id: lead.id,
  katastrskaObcina: lead.katastrskaObcina || lead.katastrska_obcina,
  idParceleStavbe: lead.idParceleStavbe || lead.id_parcele_stavbe,
  idStavbe: lead.idStavbe || lead.id_stavbe || '',
  naslov: lead.naslov || '',
  type: lead.type,
  status: lead.status,
  zkDate: formatDateForDisplay(lead.zkDate || lead.zk_date),
  zkExtractId: lead.zkExtractId || lead.zk_extract_id,
  notes: lead.notes,
  ownerNames: lead.ownerNames || lead.owner_names,
  koName: lead.koName || lead.ko_name || '',
  owners: (lead.owners || []).map((o) => ({
    id: o.id,
    contactId: o.contactId || o.contact_id || null,
    name: o.name,
    share: o.share,
    type: o.type,
    emso: o.emso,
    dateOfBirth: formatDateForDisplay(o.dateOfBirth || o.date_of_birth),
    address: o.address,
    phone: o.phone || '',
    email: o.email || '',
    notes: o.notes || ''
  })),
  interactions: (lead.interactions || []).map((i) => ({
    date: i.date,
    type: i.type,
    notes: i.notes
  }))
});

const transformColdCallingLead = (lead) => ({
  id: lead.id,
  name: lead.name || '',
  katastrskaObcina: lead.katastrskaObcina || lead.katastrska_obcina || '',
  koName: lead.koName || lead.ko_name || '',
  idParceleStavbe: lead.idParceleStavbe || lead.id_parcele_stavbe || '',
  kraj: lead.kraj || '',
  idStavbe: lead.idStavbe || lead.id_stavbe || '',
  naslov: lead.naslov || '',
  type: lead.type || DEFAULT_COLD_CALLING_TYPES[0],
  ownerNames: lead.ownerNames || lead.owner_names || '',
  owners: (lead.owners || lead.owners_json || []).map((owner) => ({
    name: owner.name || '',
    share: owner.share || '',
    type: owner.type || '',
    emso: owner.emso || null,
    dateOfBirth: formatDateForDisplay(owner.dateOfBirth || owner.date_of_birth),
    address: owner.address || null,
    phone: owner.phone || '',
    email: owner.email || '',
    notes: owner.notes || ''
  })),
  phone: lead.phone || '',
  email: lead.email || '',
  listingLink: lead.listingLink || lead.listing_link || '',
  notes: lead.notes || '',
  status: lead.status || 'Novo',
  zkDate: formatDateForDisplay(lead.zkDate || lead.zk_date),
  zkExtractId: lead.zkExtractId || lead.zk_extract_id || null,
  lastContact: formatDateForInput(lead.lastContact || lead.last_contact)
});

const sendWithContactResolution = async (makeRequest, initialPayload) => {
  let payload = initialPayload;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const response = await makeRequest(payload);
    if (response.ok) {
      return response;
    }

    const conflict = await resolveContactConflictResponse(response, payload);
    if (!conflict.shouldRetry) {
      return response;
    }

    payload = conflict.payload;
  }

  return makeRequest(payload);
};

const LeadGen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // View toggle: 'properties' or 'coldCalling'
  const [viewMode, setViewMode] = useState('properties');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addingToType, setAddingToType] = useState(''); // 'properties' or 'coldCalling'

  // Filter states
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCreator, setFilterCreator] = useState('');
  const [filterKo, setFilterKo] = useState('');
  const [coldCallFilterStatus, setColdCallFilterStatus] = useState('');
  const [coldCallFilterType, setColdCallFilterType] = useState('');
  const [coldCallFilterKo, setColdCallFilterKo] = useState('');

  // Property types and status options (can be extended)
  const [propertyTypes, setPropertyTypes] = useState(() => loadStoredTypes(PROPERTY_TYPES_STORAGE_KEY, DEFAULT_PROPERTY_TYPES));
  const [coldCallingTypes] = useState(DEFAULT_COLD_CALLING_TYPES);
  const [propertyStatusOptions, setPropertyStatusOptions] = useState([
    { name: 'Novo', color: '#3b82f6' },
    { name: 'Enquiry sent', color: '#f59e0b' },
    { name: 'Closed', color: '#64748b' }
  ]);
  const [coldCallingStatusOptions, setColdCallingStatusOptions] = useState([
    { name: 'Novo', color: '#3b82f6' },
    { name: 'Enquiry sent', color: '#f59e0b' },
    { name: 'Closed', color: '#64748b' }
  ]);

  // Data for Interesting Properties
  const [propertiesLeads, setPropertiesLeads] = useState([]);
  const [allPropertyLeads, setAllPropertyLeads] = useState([]);

  // Modal state
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [selectedColdCall, setSelectedColdCall] = useState(null);
  const [showColdCallDetailModal, setShowColdCallDetailModal] = useState(false);
  const [isEditingColdCallDetails, setIsEditingColdCallDetails] = useState(false);
  const [pastSales, setPastSales] = useState([]);
  const [pastSalesLoading, setPastSalesLoading] = useState(false);
  const [refreshingPastSales, setRefreshingPastSales] = useState(false);
  const [coldCallPastSales, setColdCallPastSales] = useState([]);
  const [coldCallPastSalesLoading, setColdCallPastSalesLoading] = useState(false);
  const [refreshingColdCallPastSales, setRefreshingColdCallPastSales] = useState(false);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [newTicketNote, setNewTicketNote] = useState('');
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistTemplateItems, setChecklistTemplateItems] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [hasAddedPropertyChecklistSection, setHasAddedPropertyChecklistSection] = useState(false);
  const [hasExistingLeadMptChecklist, setHasExistingLeadMptChecklist] = useState(false);
  const [hasAddedLeadMptChecklist, setHasAddedLeadMptChecklist] = useState(false);
  const [showColdCallChecklistModal, setShowColdCallChecklistModal] = useState(false);
  const [coldCallChecklistTemplateItems, setColdCallChecklistTemplateItems] = useState([]);
  const [coldCallChecklistValues, setColdCallChecklistValues] = useState({});
  const [coldCallChecklistLoading, setColdCallChecklistLoading] = useState(false);
  const [coldCallChecklistSaving, setColdCallChecklistSaving] = useState(false);

  // Document upload state
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingColdCallDocument, setUploadingColdCallDocument] = useState(false);

  // Interaction state
  const [newInteractionType, setNewInteractionType] = useState('Call');
  const [newInteractionNotes, setNewInteractionNotes] = useState('');
  const [coldCallTickets, setColdCallTickets] = useState([]);
  const [newColdCallTicketNote, setNewColdCallTicketNote] = useState('');
  const [newColdCallInteractionType, setNewColdCallInteractionType] = useState('Call');
  const [newColdCallInteractionNotes, setNewColdCallInteractionNotes] = useState('');

  // Letter popup state
  const [showLetterPopup, setShowLetterPopup] = useState(false);
  const [letterOptions, setLetterOptions] = useState({ gender: 'male', location: '', owner: null, address: '', parcelCount: 1, extraParcel2: '', extraParcel3: '' });
  
  // Status management
  const [editingStatusIndex, setEditingStatusIndex] = useState(null);
  const [editingStatusValue, setEditingStatusValue] = useState('');
  const [editingStatusColor, setEditingStatusColor] = useState('#64748b');
  const [typeManagerScope, setTypeManagerScope] = useState('properties');
  const [statusManagerScope, setStatusManagerScope] = useState('properties');

  // Data for Cold Calling
  const [coldCallingLeads, setColdCallingLeads] = useState([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [highlightedColdCallingLeadId, setHighlightedColdCallingLeadId] = useState(null);
  const leadIdOpenedRef = useRef('');
  const coldCallingLeadIdOpenedRef = useRef('');

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!showDetailModal || !selectedLead || !supportsPastSales(selectedLead.type)) {
      setPastSales([]);
      setPastSalesLoading(false);
      setRefreshingPastSales(false);
      return;
    }

    let isCancelled = false;

    const fetchPastSales = async () => {
      setPastSalesLoading(true);
      try {
        const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/past-sales`);
        if (!response.ok) {
          throw new Error('Failed to load past sales');
        }

        const data = await response.json();
        if (!isCancelled) {
          setPastSales(Array.isArray(data.sales) ? data.sales : []);
        }
      } catch (error) {
        console.error('Error fetching past sales:', error);
        if (!isCancelled) {
          setPastSales([]);
        }
      } finally {
        if (!isCancelled) {
          setPastSalesLoading(false);
        }
      }
    };

    fetchPastSales();

    return () => {
      isCancelled = true;
    };
  }, [showDetailModal, selectedLead]);

  useEffect(() => {
    if (!showColdCallDetailModal || !selectedColdCall || !supportsPastSales(selectedColdCall.type)) {
      setColdCallPastSales([]);
      setColdCallPastSalesLoading(false);
      setRefreshingColdCallPastSales(false);
      return;
    }

    let isCancelled = false;

    const fetchPastSales = async () => {
      setColdCallPastSalesLoading(true);
      try {
        const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/past-sales`);
        if (!response.ok) {
          throw new Error('Failed to load past sales');
        }

        const data = await response.json();
        if (!isCancelled) {
          setColdCallPastSales(Array.isArray(data.sales) ? data.sales : []);
        }
      } catch (error) {
        console.error('Error fetching cold call past sales:', error);
        if (!isCancelled) {
          setColdCallPastSales([]);
        }
      } finally {
        if (!isCancelled) {
          setColdCallPastSalesLoading(false);
        }
      }
    };

    fetchPastSales();

    return () => {
      isCancelled = true;
    };
  }, [showColdCallDetailModal, selectedColdCall]);

  const handleRefreshPastSales = async () => {
    if (!selectedLead || !supportsPastSales(selectedLead.type)) {
      return;
    }

    setRefreshingPastSales(true);
    try {
      const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/past-sales?forceRefresh=1`);
      if (!response.ok) {
        throw new Error('Failed to refresh past sales');
      }

      const data = await response.json();
      setPastSales(Array.isArray(data.sales) ? data.sales : []);
    } catch (error) {
      console.error('Error refreshing past sales:', error);
      alert('Error refreshing past sales: ' + error.message);
    } finally {
      setRefreshingPastSales(false);
    }
  };

  const handleRefreshColdCallPastSales = async () => {
    if (!selectedColdCall || !supportsPastSales(selectedColdCall.type)) {
      return;
    }

    setRefreshingColdCallPastSales(true);
    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/past-sales?forceRefresh=1`);
      if (!response.ok) {
        throw new Error('Failed to refresh past sales');
      }

      const data = await response.json();
      setColdCallPastSales(Array.isArray(data.sales) ? data.sales : []);
    } catch (error) {
      console.error('Error refreshing cold call past sales:', error);
      alert('Error refreshing past sales: ' + error.message);
    } finally {
      setRefreshingColdCallPastSales(false);
    }
  };

  const buildPropertyPayload = (lead) => ({
    katastrskaObcina: lead.katastrskaObcina,
    idParceleStavbe: lead.idParceleStavbe,
    idStavbe: lead.idStavbe || '',
    naslov: lead.naslov || '',
    type: lead.type,
    status: lead.status,
    ownerNames: lead.ownerNames,
    zkDate: lead.zkDate,
    zkExtractId: lead.zkExtractId,
    notes: lead.notes || '',
    userName: localStorage.getItem('leadGenUser') || 'User',
    owners: (lead.owners || []).map((owner) => ({
      id: owner.id,
      name: owner.name,
      share: owner.share,
      type: owner.type,
      emso: owner.emso,
      dateOfBirth: owner.dateOfBirth,
      address: owner.address,
      phone: owner.phone || '',
      email: owner.email || '',
      notes: owner.notes || ''
    })),
    interactions: (lead.interactions || []).map((interaction) => ({
      date: interaction.date,
      type: interaction.type,
      notes: interaction.notes
    }))
  });

  const buildColdCallPayload = (lead) => ({
    name: lead.name || '',
    katastrskaObcina: lead.katastrskaObcina || '',
    idParceleStavbe: lead.idParceleStavbe || '',
    kraj: lead.kraj || '',
    idStavbe: lead.idStavbe || '',
    naslov: lead.naslov || '',
    type: lead.type || '',
    ownerNames: lead.ownerNames || '',
    owners: (lead.owners || []).map((owner) => ({
      name: owner.name || '',
      share: owner.share || '',
      type: owner.type || '',
      emso: owner.emso || null,
      dateOfBirth: owner.dateOfBirth || '',
      address: owner.address || null,
      phone: owner.phone || '',
      email: owner.email || '',
      notes: owner.notes || ''
    })),
    phone: lead.phone || '',
    email: lead.email || '',
    listingLink: lead.listingLink || '',
    notes: lead.notes || '',
    status: lead.customStatus || lead.status || 'Novo',
    zkDate: lead.zkDate || '',
    zkExtractId: lead.zkExtractId || null,
    lastContact: lead.lastContact || ''
  });

  const persistPropertyLead = async (lead) => {
    const payload = buildPropertyPayload(lead);

    const response = await sendWithContactResolution(
      (nextPayload) => authFetch(`${API_BASE}/api/leads/properties/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextPayload)
      }),
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to save property lead');
    }
  };

  const persistColdCallingLead = async (lead) => {
    const payload = buildColdCallPayload(lead);

    const response = await sendWithContactResolution(
      (nextPayload) => authFetch(`${API_BASE}/api/leads/cold-calling/${lead.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nextPayload)
      }),
      payload
    );

    if (!response.ok) {
      throw new Error('Failed to save cold calling lead');
    }
  };

  // Copy KO and parcel/stavba ID to clipboard in format "KO ID"
  const handleCopyKoId = async (lead) => {
    const text = `${lead.katastrskaObcina} ${lead.idParceleStavbe}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy KO ID:', err);
      alert('Could not copy KO ID');
    }
  };

  const handleToggleEdit = async () => {
    if (!selectedLead) return;

    if (isEditingDetails) {
      try {
        await persistPropertyLead(selectedLead);
        await fetchPropertyLeads();
        await fetchTickets(selectedLead.id);
        setIsEditingDetails(false);
      } catch (error) {
        console.error('Error saving changes:', error);
        alert('Error saving changes: ' + error.message);
      }
    } else {
      setIsEditingDetails(true);
    }
  };

  const openColdCallDetail = (lead) => {
    setSelectedColdCall({ ...lead });
    setHighlightedColdCallingLeadId(lead.id);
    setShowColdCallDetailModal(true);
    setIsEditingColdCallDetails(false);
    fetchColdCallTickets(lead.id);
  };

  const handleToggleColdCallEdit = async () => {
    if (!selectedColdCall) return;

    if (isEditingColdCallDetails) {
      if (!String(selectedColdCall.phone || '').trim()) {
        alert('Phone is required');
        return;
      }

      try {
        await persistColdCallingLead(selectedColdCall);
        const refreshed = await fetchColdCallingLeads();
        const updatedLead = refreshed.find((lead) => String(lead.id) === String(selectedColdCall.id));
        setSelectedColdCall(updatedLead || selectedColdCall);
        setIsEditingColdCallDetails(false);
      } catch (error) {
        console.error('Error saving cold call changes:', error);
        alert('Error saving changes: ' + error.message);
      }
    } else {
      setIsEditingColdCallDetails(true);
    }
  };

  const handleColdCallStatusChange = async (lead, newStatus) => {
    const updatedLead = { ...lead, status: newStatus };
    setColdCallingLeads((previous) => previous.map((entry) => (entry.id === lead.id ? updatedLead : entry)));
    if (selectedColdCall?.id === lead.id) {
      setSelectedColdCall((previous) => previous ? { ...previous, status: newStatus } : previous);
    }

    try {
      await persistColdCallingLead(updatedLead);
      await fetchColdCallingLeads();
    } catch (error) {
      console.error('Error updating cold call status:', error);
      alert('Failed to update cold call status.');
      await fetchColdCallingLeads();
    }
  };

  useEffect(() => {
    fetchColdCallingLeads();
    fetchPropertyStatusOptions();
    fetchColdCallingStatusOptions();
  }, []);

  useEffect(() => {
    fetchPropertyLeads();
  }, [filterStatus, filterType, filterCreator, filterKo]);

  useEffect(() => {
    const leadIdParam = searchParams.get('leadId');
    if (!leadIdParam) {
      leadIdOpenedRef.current = '';
      return;
    }

    if (leadIdOpenedRef.current === String(leadIdParam)) return;

    const match = propertiesLeads.find((lead) => String(lead.id) === String(leadIdParam));
    if (match) {
      setViewMode('properties');
      setSelectedLead(match);
      setShowDetailModal(true);
      fetchTickets(match.id);
      leadIdOpenedRef.current = String(leadIdParam);
    }
  }, [propertiesLeads, searchParams]);

  useEffect(() => {
    const coldCallingLeadIdParam = searchParams.get('coldCallingLeadId');
    if (!coldCallingLeadIdParam) {
      coldCallingLeadIdOpenedRef.current = '';
      setHighlightedColdCallingLeadId(null);
      return;
    }

    if (coldCallingLeadIdOpenedRef.current === String(coldCallingLeadIdParam)) return;

    const match = coldCallingLeads.find((lead) => String(lead.id) === String(coldCallingLeadIdParam));
    if (!match) return;

    setViewMode('coldCalling');
    setHighlightedColdCallingLeadId(match.id);
  setSelectedColdCall({ ...match });
  setShowColdCallDetailModal(true);
    coldCallingLeadIdOpenedRef.current = String(coldCallingLeadIdParam);

    const timeoutId = window.setTimeout(() => {
      const target = document.getElementById(`cold-calling-lead-${match.id}`);
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [coldCallingLeads, searchParams]);

  const fetchPropertyStatusOptions = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/status-options`);
      if (response.ok) {
        const statuses = await response.json();
        setPropertyStatusOptions(Array.isArray(statuses) ? statuses.map(normalizeStatusOption).filter((status) => status.name) : []);
      }
    } catch (err) {
      console.error('Error fetching status options:', err);
    }
  };

  const fetchColdCallingStatusOptions = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling/status-options`);
      if (response.ok) {
        const statuses = await response.json();
        setColdCallingStatusOptions(Array.isArray(statuses) ? statuses.map(normalizeStatusOption).filter((status) => status.name) : []);
      }
    } catch (err) {
      console.error('Error fetching cold calling status options:', err);
    }
  };

  const fetchPropertyLeads = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE}/api/leads/properties`;
      const params = [];
      
      if (filterStatus) {
        params.push(`status=${encodeURIComponent(filterStatus)}`);
      }
      if (filterType) {
        params.push(`type=${encodeURIComponent(filterType)}`);
      }
      if (filterCreator) {
        params.push(`creator=${encodeURIComponent(filterCreator)}`);
      }
      if (filterKo) {
        params.push(`ko=${encodeURIComponent(filterKo)}`);
      }
      
      if (params.length > 0) {
        url += '?' + params.join('&');
      }
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        const transformed = data.map(transformPropertyLead);
        setPropertiesLeads(transformed);
        if (!filterStatus && !filterType && !filterCreator && !filterKo) {
          setAllPropertyLeads(transformed);
        }
      }
    } catch (error) {
      console.error('Error fetching property leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPropertyLeadsUnfiltered = async () => {
    const response = await authFetch(`${API_BASE}/api/leads/properties`);
    if (!response.ok) {
      throw new Error('Failed to fetch property leads');
    }
    const data = await response.json();
    return data.map(transformPropertyLead);
  };

  const refreshAllPropertyLeads = async () => {
    try {
      const allLeads = await fetchAllPropertyLeadsUnfiltered();
      setAllPropertyLeads(allLeads);
    } catch (error) {
      console.error('Error fetching all property leads:', error);
    }
  };

  const loadChecklistData = async (lead = selectedLead) => {
    if (!lead?.id || !lead?.type) {
      setChecklistTemplateItems([]);
      setChecklistValues({});
      return;
    }

    setChecklistLoading(true);
    try {
      const params = new URLSearchParams({
        scope: CHECKLIST_SCOPE,
        typeKey: lead.type,
        entityType: CHECKLIST_ENTITY_TYPE,
        entityId: String(lead.id)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load checklist');
      }

      const data = await response.json();
      setChecklistTemplateItems(Array.isArray(data.items) ? data.items : []);
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
    } catch (error) {
      console.error('Error loading checklist:', error);
      setChecklistTemplateItems([]);
      setChecklistValues({});
    } finally {
      setChecklistLoading(false);
    }
  };

  const loadColdCallChecklistData = async (lead = selectedColdCall) => {
    if (!lead?.id || !lead?.type) {
      setColdCallChecklistTemplateItems([]);
      setColdCallChecklistValues({});
      return;
    }

    setColdCallChecklistLoading(true);
    try {
      const params = new URLSearchParams({
        scope: COLD_CALL_CHECKLIST_SCOPE,
        typeKey: lead.type,
        entityType: COLD_CALL_CHECKLIST_ENTITY_TYPE,
        entityId: String(lead.id)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load checklist');
      }

      const data = await response.json();
      setColdCallChecklistTemplateItems(Array.isArray(data.items) ? data.items : []);
      setColdCallChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
    } catch (error) {
      console.error('Error loading cold call checklist:', error);
      setColdCallChecklistTemplateItems([]);
      setColdCallChecklistValues({});
    } finally {
      setColdCallChecklistLoading(false);
    }
  };

  const loadLeadMasterParcelChecklistState = async (lead = selectedLead) => {
    if (!lead?.id) {
      setHasExistingLeadMptChecklist(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        scope: 'lead_gen_property_mpt',
        typeKey: 'mpt',
        entityType: CHECKLIST_ENTITY_TYPE,
        entityId: String(lead.id)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to load MPT checklist');
      }

      const data = await response.json();
      const values = data.values && typeof data.values === 'object' ? data.values : {};
      setHasExistingLeadMptChecklist(Object.keys(values).length > 0);
    } catch (error) {
      console.error('Error loading lead MPT checklist:', error);
      setHasExistingLeadMptChecklist(false);
    }
  };

  useEffect(() => {
    if (!showDetailModal || !selectedLead?.id || !selectedLead?.type) {
      setChecklistTemplateItems([]);
      setChecklistValues({});
      setHasAddedPropertyChecklistSection(false);
      setHasExistingLeadMptChecklist(false);
      setHasAddedLeadMptChecklist(false);
      return;
    }

    loadChecklistData(selectedLead);
    loadLeadMasterParcelChecklistState(selectedLead);
    setHasAddedPropertyChecklistSection(false);
    setHasAddedLeadMptChecklist(false);
  }, [showDetailModal, selectedLead?.id, selectedLead?.type]);

  useEffect(() => {
    if (!showColdCallDetailModal || !selectedColdCall?.id || !selectedColdCall?.type) {
      setColdCallChecklistTemplateItems([]);
      setColdCallChecklistValues({});
      return;
    }

    loadColdCallChecklistData(selectedColdCall);
  }, [showColdCallDetailModal, selectedColdCall?.id, selectedColdCall?.type]);

  const fetchColdCallingLeads = async () => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling`);
      if (response.ok) {
        const data = await response.json();
        const transformed = data.map(transformColdCallingLead);
        setColdCallingLeads(transformed);
        return transformed;
      }
    } catch (error) {
      console.error('Error fetching cold calling leads:', error);
    }

    return [];
  };

  const getStatusColor = (status, options = propertyStatusOptions) => {
    const normalizedStatus = String(status || '').trim();
    const matchedStatus = options.find((option) => getStatusOptionName(option) === normalizedStatus);
    return matchedStatus ? getStatusOptionColor(matchedStatus) : getDefaultStatusColor(normalizedStatus);
  };

  const buildStatusOptionsForLead = (currentStatus, optionsSource = propertyStatusOptions) => {
    const options = optionsSource.map(normalizeStatusOption);
    if (currentStatus && !options.some((option) => option.name === currentStatus)) {
      options.push({
        name: currentStatus,
        color: getDefaultStatusColor(currentStatus)
      });
    }
    return options;
  };

  const filteredPropertiesLeads = propertiesLeads.filter(lead => 
    (lead.katastrskaObcina || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.idParceleStavbe || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (lead.ownerNames || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredColdCallingLeads = coldCallingLeads.filter((lead) => {
    const normalizedSearch = searchTerm.toLowerCase();
    const normalizedColdCallKoFilter = coldCallFilterKo.toLowerCase();
    const matchesSearch =
      (lead.katastrskaObcina || '').toLowerCase().includes(normalizedSearch) ||
      (lead.koName || '').toLowerCase().includes(normalizedSearch) ||
      (lead.idParceleStavbe || '').toLowerCase().includes(normalizedSearch) ||
      (lead.naslov || '').toLowerCase().includes(normalizedSearch) ||
      (lead.name || '').toLowerCase().includes(normalizedSearch) ||
      (lead.phone || '').toLowerCase().includes(normalizedSearch) ||
      (lead.email || '').toLowerCase().includes(normalizedSearch) ||
      (lead.listingLink || '').toLowerCase().includes(normalizedSearch);

    const matchesStatus = !coldCallFilterStatus || lead.status === coldCallFilterStatus;
    const matchesType = !coldCallFilterType || lead.type === coldCallFilterType;
    const matchesKo = !coldCallFilterKo
      || (lead.katastrskaObcina || '').toLowerCase().includes(normalizedColdCallKoFilter)
      || (lead.koName || '').toLowerCase().includes(normalizedColdCallKoFilter)
      || (lead.idParceleStavbe || '').toLowerCase().includes(normalizedColdCallKoFilter);

    return matchesSearch && matchesStatus && matchesType && matchesKo;
  });

  const ownerOtherPropertiesMap = useMemo(() => {
    if (!selectedLead) return {};

    const sourceLeads = allPropertyLeads.length > 0 ? allPropertyLeads : propertiesLeads;
    const map = {};
    const selectedId = selectedLead.id;

    sourceLeads.forEach((lead) => {
      if (!lead || (selectedId && lead.id === selectedId)) return;

      const ownerNameList = [];
      if (lead.owners && lead.owners.length > 0) {
        ownerNameList.push(
          ...lead.owners.map((owner) => owner?.name).filter(Boolean)
        );
      }
      if (lead.ownerNames) {
        const splitNames = String(lead.ownerNames)
          .split(/[,;\n]+/)
          .map((name) => name.trim())
          .filter(Boolean);
        ownerNameList.push(...splitNames);
      }

      ownerNameList.forEach((name) => {
        const key = normalizeOwnerName(name);
        if (!key) return;
        if (!map[key]) map[key] = [];
        map[key].push(lead);
      });
    });

    return map;
  }, [selectedLead, allPropertyLeads, propertiesLeads]);

  const filledChecklistItems = useMemo(() => checklistTemplateItems
    .map((item) => ({
      item,
      value: checklistValues[item.id]
    }))
    .filter(({ item, value }) => isChecklistValueFilled(item, value)), [checklistTemplateItems, checklistValues]);

  const filledColdCallChecklistItems = useMemo(() => coldCallChecklistTemplateItems
    .map((item) => ({
      item,
      value: coldCallChecklistValues[item.id]
    }))
    .filter(({ item, value }) => isChecklistValueFilled(item, value)), [coldCallChecklistTemplateItems, coldCallChecklistValues]);

  const hasPropertyChecklistSection = hasAddedPropertyChecklistSection || Object.keys(checklistValues).length > 0;
  const hasLeadMptChecklistSection = hasAddedLeadMptChecklist || hasExistingLeadMptChecklist;

  // Form states for adding new entries
  const [newProperty, setNewProperty] = useState(() => createDefaultNewProperty());

  const [newColdCall, setNewColdCall] = useState(() => createDefaultNewColdCall());

  const [showTypeManager, setShowTypeManager] = useState(false);
  const [newPropertyType, setNewPropertyType] = useState('');
  const [showStatusManager, setShowStatusManager] = useState(false);
  const [newStatusOption, setNewStatusOption] = useState('');
  const [newStatusOptionColor, setNewStatusOptionColor] = useState('#64748b');
  const [zkLoading, setZkLoading] = useState(false);
  const [refreshingZk, setRefreshingZk] = useState(false);

  // Fetch ZK data based on KO and ID
  const fetchZKData = async () => {
    if (!newProperty.katastrskaObcina || !newProperty.idParceleStavbe) {
      alert('Please enter both Katastrska občina and ID parcele/stavbe');
      return;
    }

    setZkLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(newProperty.katastrskaObcina)}&id=${encodeURIComponent(newProperty.idParceleStavbe)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          alert('No ZK entry found for this property. Please check the Katastrska občina and ID parcele/stavbe.');
        } else {
          alert(`Error: ${response.status}. Make sure the backend server is running.`);
        }
        return;
      }
      
      const data = await response.json();
      
      // Display preview of ZK data
      alert(`ZK Data found:\n\nOwners: ${data.ownerNames}\nDate: ${data.date}\n\nCadastral: ${data.cadastralDetails || 'N/A'}\nID Sign: ${data.idSign || 'N/A'}`);
    } catch (error) {
      console.error('Error fetching ZK data:', error);
      alert(`Could not fetch ZK data. Please check:\n1. Backend server is running on ${API_BASE}\n2. The Katastrska občina and ID are correct\n\nError: ${error.message}`);
    } finally {
      setZkLoading(false);
    }
  };

  const refreshZkDataForAllLeads = async () => {
    setRefreshingZk(true);
    try {
      const allLeads = await fetchAllPropertyLeadsUnfiltered();
      let updatedCount = 0;

      for (const lead of allLeads) {
        try {
          const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(lead.katastrskaObcina)}&id=${encodeURIComponent(lead.idParceleStavbe)}`);
          if (!response.ok) continue;

          const zkData = await response.json();
          const latestDate = parseZkDateForCompare(zkData.date);
          const currentDate = parseZkDateForCompare(lead.zkDate);
          const ownersFromZk = (zkData.owners || []).map((owner) => ({
            name: owner.name || owner.imetnik_ime || 'N/A',
            share: owner.share || owner.delez || 'N/A',
            type: owner.type || owner.vrsta_polozaja || 'N/A',
            emso: owner.emso || null,
            dateOfBirth: formatDateForDisplay(owner.dateOfBirth || owner.date_of_birth),
            address: owner.address || null,
            phone: owner.phone || '',
            notes: owner.notes || ''
          }));

          const shouldUpdate = Boolean(
            (latestDate && (!currentDate || latestDate > currentDate)) ||
            (!lead.zkExtractId && zkData.zkId) ||
            (zkData.zkId && zkData.zkId !== lead.zkExtractId) ||
            (!lead.ownerNames && zkData.ownerNames) ||
            ((lead.owners || []).length === 0 && ownersFromZk.length > 0)
          );

          if (!shouldUpdate) continue;

          const updatedLead = {
            ...lead,
            ownerNames: zkData.ownerNames || lead.ownerNames,
            zkDate: formatDateForDisplay(zkData.date || lead.zkDate),
            zkExtractId: zkData.zkId || lead.zkExtractId,
            owners: ownersFromZk.length > 0 ? ownersFromZk : lead.owners,
            koName: extractKoNameFromDetails(zkData.cadastralDetails || '') || lead.koName
          };

          await persistPropertyLead(updatedLead);
          updatedCount += 1;
        } catch (innerError) {
          console.error(`Error refreshing ZK data for lead ${lead.id}:`, innerError);
        }
      }

      await fetchPropertyLeads();
      if (updatedCount === 0) {
        alert('No newer ZK entries found.');
      } else {
        alert(`Updated ${updatedCount} lead${updatedCount === 1 ? '' : 's'} with newer ZK data.`);
      }
    } catch (error) {
      console.error('Error refreshing ZK data:', error);
      alert('Error refreshing ZK data: ' + error.message);
    } finally {
      setRefreshingZk(false);
    }
  };

  // Add new property lead
  const handleAddProperty = async () => {
    if (!newProperty.katastrskaObcina || !newProperty.idParceleStavbe) {
      alert('Please fill in required fields');
      return;
    }

    const trimmedKo = newProperty.katastrskaObcina.trim();
    const trimmedId = newProperty.idParceleStavbe.trim();
    if (!trimmedKo || !trimmedId) {
      alert('Please fill in required fields');
      return;
    }

    try {
      const allLeads = allPropertyLeads.length > 0
        ? allPropertyLeads
        : await fetchAllPropertyLeadsUnfiltered();

      const newKey = buildPropertyKey(trimmedKo, trimmedId);
      const duplicateLead = allLeads.find(
        (lead) => buildPropertyKey(lead.katastrskaObcina, lead.idParceleStavbe) === newKey
      );

      if (duplicateLead) {
        alert(`This property already exists (KO ${duplicateLead.katastrskaObcina}, ID ${duplicateLead.idParceleStavbe}).`);
        return;
      }
    } catch (error) {
      console.error('Error checking existing properties:', error);
      alert('Unable to verify existing properties. Please try again.');
      return;
    }

    // Fetch ZK data first
    setZkLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(newProperty.katastrskaObcina)}&id=${encodeURIComponent(newProperty.idParceleStavbe)}`);
      
      let zkData = { ownerNames: 'N/A', date: null, owners: [] };
      
      if (response.ok) {
        zkData = await response.json();
        console.log('ZK data received:', zkData);
      } else {
        console.warn('ZK data not found (status: ' + response.status + '), using N/A values');
      }

      const finalStatus = newProperty.customStatus || newProperty.status;
      
      // Ensure zkDate is properly formatted - backend will handle the formatting
      const zkDateValue = (zkData.date && zkData.date !== 'N/A') ? zkData.date : null;
      console.log('zkDateValue being sent:', zkDateValue);
      
      const currentUser = localStorage.getItem('leadGenUser');

      const propertyPayload = {
        katastrskaObcina: trimmedKo,
        idParceleStavbe: trimmedId,
        idStavbe: newProperty.idStavbe.trim(),
        naslov: newProperty.naslov.trim(),
        type: newProperty.type,
        status: finalStatus,
        ownerNames: zkData.ownerNames || 'N/A',
        zkDate: zkDateValue,
        zkExtractId: zkData.zkId || null,
        notes: '',
        userName: currentUser || undefined,
        owners: (zkData.owners || []).map(owner => ({
          name: owner.name || 'N/A',
          share: owner.share || 'N/A',
          type: owner.type || 'N/A',
          emso: owner.emso || null,
          dateOfBirth: owner.dateOfBirth || null,
          address: owner.address || null,
          phone: '',
          notes: ''
        })),
        interactions: []
      };

      console.log('Payload being sent:', propertyPayload);

      // Save to database
      const saveResponse = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/properties`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        propertyPayload
      );

      if (saveResponse.ok) {
        const savedLead = await saveResponse.json();
        rememberLeadGenKo(trimmedKo);
        rememberLeadType(newProperty.type);
        // Refresh the leads list
        fetchPropertyLeads();
        setShowAddModal(false);
        setNewProperty(createDefaultNewProperty());
      } else {
        alert('Failed to save property lead');
      }
    } catch (error) {
      console.error('Error adding property:', error);
      alert('Error adding property: ' + error.message);
    } finally {
      setZkLoading(false);
    }
  };

  // Add new cold calling lead
  const handleAddColdCall = async () => {
    if (!String(newColdCall.phone || '').trim()) {
      alert('Please fill in the required field (Phone)');
      return;
    }

    const finalStatus = newColdCall.customStatus || newColdCall.status;
    
    const coldCallPayload = {
      type: newColdCall.type,
      phone: newColdCall.phone,
      listingLink: newColdCall.listingLink,
      status: finalStatus,
      name: '',
      katastrskaObcina: '',
      idParceleStavbe: '',
      kraj: newColdCall.kraj || '',
      idStavbe: '',
      naslov: '',
      ownerNames: '',
      owners: [],
      email: '',
      notes: '',
      zkDate: '',
      zkExtractId: null,
      lastContact: ''
    };

    try {
      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/cold-calling`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        coldCallPayload
      );

      if (response.ok) {
        fetchColdCallingLeads();
        setShowAddModal(false);
        setNewColdCall(createDefaultNewColdCall());
      } else {
        alert('Failed to save cold calling lead');
      }
    } catch (error) {
      console.error('Error adding cold call:', error);
      alert('Error adding cold calling lead: ' + error.message);
    }
  };

  // Add new property type
  const handleAddPropertyType = () => {
    const trimmedType = newPropertyType.trim();
    if (!trimmedType) return;

    if (inferredManagerScope === 'coldCalling') {
      return;
    } else {
      if (propertyTypes.includes(trimmedType)) return;
      const nextTypes = [...propertyTypes, trimmedType];
      setPropertyTypes(nextTypes);
      persistStoredTypes(PROPERTY_TYPES_STORAGE_KEY, nextTypes);
    }

      setNewPropertyType('');
      setShowTypeManager(false);
  };

  // Add new status option
  const handleAddStatusOption = async () => {
    const trimmedStatus = newStatusOption.trim();
    const scopeIsCold = inferredManagerScope === 'coldCalling';
    const optionSource = scopeIsCold ? coldCallingStatusOptions : propertyStatusOptions;
    const endpoint = scopeIsCold ? `${API_BASE}/api/leads/cold-calling/status-options` : `${API_BASE}/api/leads/status-options`;

    if (trimmedStatus && !optionSource.some((status) => getStatusOptionName(status) === trimmedStatus)) {
      try {
        const response = await authFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ statusName: trimmedStatus, color: newStatusOptionColor })
        });

        if (response.ok) {
          const createdStatus = normalizeStatusOption(await response.json());
          if (scopeIsCold) {
            setColdCallingStatusOptions([...coldCallingStatusOptions, createdStatus]);
          } else {
            setPropertyStatusOptions([...propertyStatusOptions, createdStatus]);
          }
          setNewStatusOption('');
          setNewStatusOptionColor('#64748b');
          setShowStatusManager(false);
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to add status');
        }
      } catch (error) {
        console.error('Error adding status:', error);
        alert('Error adding status: ' + error.message);
      }
    }
  };

  // Delete property lead
  const handleDeleteProperty = async (id) => {
    if (window.confirm('Are you sure you want to delete this property lead?')) {
      try {
        const response = await authFetch(`${API_BASE}/api/leads/properties/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          fetchPropertyLeads();
        } else {
          alert('Failed to delete property lead');
        }
      } catch (error) {
        console.error('Error deleting property:', error);
        alert('Error deleting property: ' + error.message);
      }
    }
  };

  const handleSaveChecklistValues = async (nextValues) => {
    if (!selectedLead?.id || !selectedLead?.type) {
      return;
    }

    setChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/entry`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: CHECKLIST_SCOPE,
          typeKey: selectedLead.type,
          entityType: CHECKLIST_ENTITY_TYPE,
          entityId: selectedLead.id,
          values: nextValues,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist');
      }

      const data = await response.json();
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
      setShowChecklistModal(false);
      setHasAddedPropertyChecklistSection(true);
    } catch (error) {
      console.error('Error saving checklist:', error);
      alert('Error saving checklist: ' + error.message);
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleSaveChecklistTemplate = async (items) => {
    if (!selectedLead?.type) {
      return;
    }

    setChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: CHECKLIST_SCOPE,
          typeKey: selectedLead.type,
          items
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist template');
      }

      const data = await response.json();
      setChecklistTemplateItems(Array.isArray(data.items) ? data.items : []);
      await loadChecklistData(selectedLead);
      setHasAddedPropertyChecklistSection(true);
    } catch (error) {
      console.error('Error saving checklist template:', error);
      alert('Error saving checklist template: ' + error.message);
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleSaveColdCallChecklistValues = async (nextValues) => {
    if (!selectedColdCall?.id || !selectedColdCall?.type) {
      return;
    }

    setColdCallChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/entry`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: COLD_CALL_CHECKLIST_SCOPE,
          typeKey: selectedColdCall.type,
          entityType: COLD_CALL_CHECKLIST_ENTITY_TYPE,
          entityId: selectedColdCall.id,
          values: nextValues,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist');
      }

      const data = await response.json();
      setColdCallChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
      setShowColdCallChecklistModal(false);
    } catch (error) {
      console.error('Error saving cold call checklist:', error);
      alert('Error saving checklist: ' + error.message);
    } finally {
      setColdCallChecklistSaving(false);
    }
  };

  const handleSaveColdCallChecklistTemplate = async (items) => {
    if (!selectedColdCall?.type) {
      return;
    }

    setColdCallChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: COLD_CALL_CHECKLIST_SCOPE,
          typeKey: selectedColdCall.type,
          items
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist template');
      }

      const data = await response.json();
      setColdCallChecklistTemplateItems(Array.isArray(data.items) ? data.items : []);
      await loadColdCallChecklistData(selectedColdCall);
    } catch (error) {
      console.error('Error saving cold call checklist template:', error);
      alert('Error saving checklist template: ' + error.message);
    } finally {
      setColdCallChecklistSaving(false);
    }
  };

  const handlePropertyStatusChange = async (lead, newStatus) => {
    const updatedLead = { ...lead, status: newStatus };
    setPropertiesLeads((prev) => prev.map((item) => item.id === lead.id ? { ...item, status: newStatus } : item));
    if (selectedLead?.id === lead.id) {
      setSelectedLead((prev) => prev ? { ...prev, status: newStatus } : prev);
    }

    try {
      await persistPropertyLead(updatedLead);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
      fetchPropertyLeads();
    }
  };

  // Fetch tickets for a property lead
  const fetchTickets = async (leadId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/properties/${leadId}/tickets`);
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const fetchColdCallTickets = async (leadId) => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${leadId}/tickets`);
      if (response.ok) {
        const data = await response.json();
        setColdCallTickets(data);
      }
    } catch (error) {
      console.error('Error fetching cold call tickets:', error);
    }
  };

  // Add a note ticket
  const handleAddNoteTicket = async () => {
    if (!newTicketNote.trim() || !selectedLead) return;
    
    try {
      const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: 'note',
          noteContent: newTicketNote,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });
      
      if (response.ok) {
        setNewTicketNote('');
        await fetchTickets(selectedLead.id);
      }
    } catch (error) {
      console.error('Error adding note ticket:', error);
      alert('Failed to add note. Please try again.');
    }
  };

  const handleAddColdCallNoteTicket = async () => {
    if (!newColdCallTicketNote.trim() || !selectedColdCall) return;

    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: 'note',
          noteContent: newColdCallTicketNote,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });

      if (response.ok) {
        setNewColdCallTicketNote('');
        await fetchColdCallTickets(selectedColdCall.id);
      }
    } catch (error) {
      console.error('Error adding cold call note ticket:', error);
      alert('Failed to add note. Please try again.');
    }
  };

  // Format ticket for display
  const formatTicket = (ticket) => {
    if (ticket.ticketType === 'note') {
      return {
        icon: '📝',
        title: 'Note Added',
        content: ticket.noteContent,
        color: '#3b82f6'
      };
    } else if (ticket.ticketType === 'change') {
      return {
        icon: '✏️',
        title: `Changed ${ticket.fieldName}`,
        content: `From "${ticket.oldValue}" to "${ticket.newValue}"`,
        color: '#f59e0b'
      };
    } else if (ticket.ticketType === 'interaction') {
      return {
        icon: '💬',
        title: 'Interaction',
        content: ticket.noteContent,
        color: '#10b981'
      };
    } else if (ticket.ticketType === 'document') {
      return {
        icon: '📎',
        title: 'Document Uploaded',
        content: ticket.documentName,
        color: '#8b5cf6',
        documentPath: ticket.documentPath
      };
    }
    return {
      icon: '📋',
      title: 'Activity',
      content: ticket.noteContent || 'Activity recorded',
      color: '#64748b'
    };
  };

  // Handle document upload
  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedLead) return;

    setUploadingDocument(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('userName', localStorage.getItem('leadGenUser') || 'User');

    try {
      const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await fetchTickets(selectedLead.id);
        event.target.value = ''; // Reset file input
        alert('Document uploaded successfully!');
      } else {
        const error = await response.json();
        console.error('Upload error:', error);
        alert(`Failed to upload document: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Error uploading document: ' + error.message);
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleColdCallDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedColdCall) return;

    setUploadingColdCallDocument(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('userName', localStorage.getItem('leadGenUser') || 'User');

    try {
      const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/documents`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        await fetchColdCallTickets(selectedColdCall.id);
        event.target.value = '';
        alert('Document uploaded successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to upload document: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading cold call document:', error);
      alert('Error uploading document: ' + error.message);
    } finally {
      setUploadingColdCallDocument(false);
    }
  };

  const handleChecklistDocumentUpload = async (_item, file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('document', file);

    const response = await authFetch(`${API_BASE}/api/checklists/documents`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to upload document');
    }

    const data = await response.json();
    return {
      documentName: data.documentName,
      documentPath: data.documentPath
    };
  };

  // Download document
  const handleDocumentDownload = async (documentPath, documentName) => {
    try {
      const response = await authFetch(`${API_BASE}/api/leads/documents/${documentPath}`);
      if (!response.ok) {
        alert('Failed to download document');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document: ' + error.message);
    }
  };

  // Download ZK extract
  const handleDownloadZKExtract = async () => {
    if (!selectedLead || !selectedLead.zkExtractId) {
      alert('No ZK extract available for this property');
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/download/${selectedLead.zkExtractId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          alert('ZK extract file not found');
        } else {
          alert('Failed to download ZK extract');
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZK_Extract_${selectedLead.katastrskaObcina}_${selectedLead.idParceleStavbe}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading ZK extract:', error);
      alert('Error downloading ZK extract: ' + error.message);
    }
  };

  const handleFetchColdCallZkData = async () => {
    if (!selectedColdCall?.katastrskaObcina || !selectedColdCall?.idParceleStavbe) {
      alert('Please first enter Katastrska občina and ID parcele/stavbe.');
      return;
    }

    setZkLoading(true);
    try {
      const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(selectedColdCall.katastrskaObcina)}&id=${encodeURIComponent(selectedColdCall.idParceleStavbe)}`);
      if (!response.ok) {
        if (response.status === 404) {
          alert('No ZK entry found for this lead.');
          return;
        }
        throw new Error('Failed to fetch ZK data');
      }

      const data = await response.json();
      const nextLead = {
        ...selectedColdCall,
        ownerNames: data.ownerNames || selectedColdCall.ownerNames || '',
        owners: Array.isArray(data.owners)
          ? data.owners.map((owner) => ({
              name: owner.name || '',
              share: owner.share || '',
              type: owner.type || '',
              emso: owner.emso || null,
              dateOfBirth: formatDateForDisplay(owner.dateOfBirth || owner.date_of_birth),
              address: owner.address || null,
              phone: owner.phone || '',
              email: owner.email || '',
              notes: owner.notes || ''
            }))
          : selectedColdCall.owners,
        zkDate: formatDateForDisplay(data.date || selectedColdCall.zkDate),
        zkExtractId: data.zkId || selectedColdCall.zkExtractId
      };

      await persistColdCallingLead(nextLead);
      const refreshed = await fetchColdCallingLeads();
      const updatedLead = refreshed.find((lead) => String(lead.id) === String(selectedColdCall.id)) || nextLead;
      setSelectedColdCall(updatedLead);
      await fetchColdCallTickets(selectedColdCall.id);
      alert(`ZK Data found:\n\nOwners: ${data.ownerNames || 'N/A'}\nDate: ${data.date || 'N/A'}`);
    } catch (error) {
      console.error('Error fetching cold call ZK data:', error);
      alert('Error fetching ZK data: ' + error.message);
    } finally {
      setZkLoading(false);
    }
  };

  // Download ZK history
  const handleDownloadZKHistory = async () => {
    if (!selectedLead || !selectedLead.zkExtractId) {
      alert('No ZK extract available for this property');
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/download-history/${selectedLead.zkExtractId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          alert('ZK history file not found');
        } else {
          alert('Failed to download ZK history');
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZK_History_${selectedLead.katastrskaObcina}_${selectedLead.idParceleStavbe}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading ZK history:', error);
      alert('Error downloading ZK history: ' + error.message);
    }
  };

  const handleDownloadColdCallZKExtract = async () => {
    if (!selectedColdCall || !selectedColdCall.zkExtractId) {
      alert('No ZK extract available for this lead');
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/download/${selectedColdCall.zkExtractId}`);
      if (!response.ok) {
        alert(response.status === 404 ? 'ZK extract file not found' : 'Failed to download ZK extract');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZK_Extract_${selectedColdCall.katastrskaObcina || 'lead'}_${selectedColdCall.idParceleStavbe || selectedColdCall.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading cold call ZK extract:', error);
      alert('Error downloading ZK extract: ' + error.message);
    }
  };

  const handleDownloadColdCallZKHistory = async () => {
    if (!selectedColdCall || !selectedColdCall.zkExtractId) {
      alert('No ZK extract available for this lead');
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/download-history/${selectedColdCall.zkExtractId}`);
      if (!response.ok) {
        alert(response.status === 404 ? 'ZK history file not found' : 'Failed to download ZK history');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ZK_History_${selectedColdCall.katastrskaObcina || 'lead'}_${selectedColdCall.idParceleStavbe || selectedColdCall.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading cold call ZK history:', error);
      alert('Error downloading ZK history: ' + error.message);
    }
  };

  // Export property leads to CSV
  const exportPropertiesToCSV = () => {
    if (filteredPropertiesLeads.length === 0) {
      alert('No data to export');
      return;
    }

    const csvData = filteredPropertiesLeads.map(lead => ({
      'Katastrska občina': lead.katastrskaObcina,
      'KO Name': lead.koName || '',
      'ID parcele/stavbe': lead.idParceleStavbe,
      'Type': lead.type,
      'Status': lead.status,
      'Owner Names': lead.ownerNames,
      'ZK Date': lead.zkDate
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Properties');
    XLSX.writeFile(wb, `properties_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export property leads to Excel
  const exportPropertiesToExcel = () => {
    if (filteredPropertiesLeads.length === 0) {
      alert('No data to export');
      return;
    }

    const csvData = filteredPropertiesLeads.map(lead => ({
      'Katastrska občina': lead.katastrskaObcina,
      'KO Name': lead.koName || '',
      'ID parcele/stavbe': lead.idParceleStavbe,
      'Type': lead.type,
      'Status': lead.status,
      'Owner Names': lead.ownerNames,
      'ZK Date': lead.zkDate
    }));

    const ws = XLSX.utils.json_to_sheet(csvData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Properties');
    XLSX.writeFile(wb, `properties_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportColdCallingToExcel = () => {
    if (filteredColdCallingLeads.length === 0) {
      alert('No data to export');
      return;
    }

    const rows = filteredColdCallingLeads.map((lead) => ({
      'Katastrska občina': lead.katastrskaObcina || '',
      'KO Name': lead.koName || '',
      'ID parcele/stavbe': lead.idParceleStavbe || '',
      'ID stavbe': lead.idStavbe || '',
      'Naslov': lead.naslov || '',
      'Type': lead.type || '',
      'Status': lead.status || '',
      'Contact': lead.name || '',
      'Phone': lead.phone || '',
      'Email': lead.email || '',
      'Listing link': lead.listingLink || '',
      'Owner Names': lead.ownerNames || '',
      'ZK Date': lead.zkDate || '',
      'Last Contact': lead.lastContact || '',
      'Notes': lead.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cold Calling');
    XLSX.writeFile(wb, `cold_calling_export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export property details to PDF
  const exportPropertyDetailsToPDF = () => {
    if (!selectedLead) {
      alert('No property selected');
      return;
    }

    const element = document.getElementById('property-detail-print');
    if (!element) {
      alert('Could not generate PDF');
      return;
    }

    const opt = {
      margin: 10,
      filename: `property_${selectedLead.katastrskaObcina}_${selectedLead.idParceleStavbe}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4' }
    };

    html2pdf().set(opt).from(element).save();
  };

  // Export property details to Excel
  const exportPropertyDetailsToExcel = () => {
    if (!selectedLead) {
      alert('No property selected');
      return;
    }

    const ownerData = (selectedLead.owners || []).map(owner => ({
      'Owner Name': owner.name,
      'Share': owner.share,
      'Type': owner.type,
      'Phone': owner.phone || '',
      'Address': owner.address || ''
    }));

    const propertyInfo = {
      'Katastrska občina': selectedLead.katastrskaObcina,
      'KO Name': selectedLead.koName || '',
      'ID parcele/stavbe': selectedLead.idParceleStavbe,
      'Type': selectedLead.type,
      'Status': selectedLead.status,
      'Owner Names': selectedLead.ownerNames,
      'ZK Date': selectedLead.zkDate,
      'Notes': selectedLead.notes || ''
    };

    const ws1 = XLSX.utils.json_to_sheet([propertyInfo]);
    const ws2 = XLSX.utils.json_to_sheet(ownerData);
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws1, 'Property');
    XLSX.utils.book_append_sheet(wb, ws2, 'Owners');
    
    XLSX.writeFile(wb, `property_${selectedLead.katastrskaObcina}_${selectedLead.idParceleStavbe}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Delete cold calling lead
  const handleDeleteColdCall = async (id) => {
    if (window.confirm('Are you sure you want to delete this cold calling lead?')) {
      try {
        const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          if (selectedColdCall?.id === id) {
            setShowColdCallDetailModal(false);
            setSelectedColdCall(null);
            setIsEditingColdCallDetails(false);
          }
          fetchColdCallingLeads();
        } else {
          alert('Failed to delete cold calling lead');
        }
      } catch (error) {
        console.error('Error deleting cold call:', error);
        alert('Error deleting cold calling lead: ' + error.message);
      }
    }
  };

  const inferredManagerScope = showColdCallDetailModal || (showAddModal && addingToType === 'coldCalling')
    ? 'coldCalling'
    : 'properties';
  const managedStatusOptions = inferredManagerScope === 'coldCalling' ? coldCallingStatusOptions : propertyStatusOptions;
  const managedStatusEndpoint = inferredManagerScope === 'coldCalling'
    ? `${API_BASE}/api/leads/cold-calling/status-options`
    : `${API_BASE}/api/leads/status-options`;
  const updateManagedStatusOptions = (nextOptions) => {
    if (inferredManagerScope === 'coldCalling') {
      setColdCallingStatusOptions(nextOptions);
    } else {
      setPropertyStatusOptions(nextOptions);
    }
  };
  const refreshManagedLeadList = async () => {
    if (inferredManagerScope === 'coldCalling') {
      await fetchColdCallingLeads();
      if (selectedColdCall?.id) {
        await fetchColdCallTickets(selectedColdCall.id);
      }
      return;
    }

    await fetchPropertyLeads();
    if (selectedLead?.id) {
      await fetchTickets(selectedLead.id);
    }
  };

  return (
    <div style={{ padding: isMobile ? '5px' : '20px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header with Toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h1 style={{ color: '#1e293b', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <Users size={32} color="#d97706" />
            Lead Generation
            {viewMode === 'properties' ? (
              <button
                type="button"
                onClick={() => navigate('/real-estate/lead-gen/checked-areas')}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc',
                  color: '#0f172a',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                <CheckSquare size={16} />
                Checked areas
              </button>
            ) : null}
          </h1>
          <p style={{ color: '#64748b', fontSize: '16px' }}>
            Track interesting properties and cold calling opportunities.
          </p>
        </div>

        {/* View Toggle */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '4px', 
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          display: 'flex',
          gap: '4px'
        }}>
          <button
            onClick={() => setViewMode('properties')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'properties' ? '#d97706' : 'transparent',
              color: viewMode === 'properties' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Interesting Properties
          </button>
          <button
            onClick={() => setViewMode('coldCalling')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'coldCalling' ? '#d97706' : 'transparent',
              color: viewMode === 'coldCalling' ? 'white' : '#64748b',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
          >
            Cold Calling
          </button>
        </div>
      </div>

      {/* Controls Bar */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: isMobile ? '12px' : '20px', 
        borderRadius: '12px', 
        marginBottom: isMobile ? '12px' : '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          gap: isMobile ? '8px' : '15px',
          flexWrap: 'wrap',
          alignItems: 'center',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Search */}
          <div style={{ flex: isMobile ? '1 1 auto' : '1 1 300px', minWidth: 0, maxWidth: isMobile ? '100%' : undefined, position: 'relative' }}>
          <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder={viewMode === 'properties' ? 'Search leads...' : 'Search cold calling leads...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 10px 10px 40px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Filter by Status */}
        {viewMode === 'properties' && (
          <>
            <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: filterStatus ? '#fef3c7' : 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Statuses</option>
                {propertyStatusOptions.map((status) => (
                  <option key={getStatusOptionName(status)} value={getStatusOptionName(status)}>{getStatusOptionName(status)}</option>
                ))}
              </select>
            </div>

            {/* Filter by Type */}
            <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: filterType ? '#fef3c7' : 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Types</option>
                {propertyTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: '0 1 220px', minWidth: '160px', position: 'relative' }}>
              <input
                type="text"
                value={filterKo}
                onChange={(e) => setFilterKo(e.target.value)}
                placeholder="Search KO or KO name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: filterKo ? '#fef3c7' : 'white',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Filter by Creator */}
            <div style={{ flex: '0 1 150px', minWidth: '120px' }}>
              <select
                value={filterCreator}
                onChange={(e) => setFilterCreator(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: filterCreator ? '#fef3c7' : 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Creators</option>
                <option value="M">M</option>
                <option value="B">B</option>
              </select>
            </div>

            {/* Clear Filters */}
            {(filterStatus || filterType || filterCreator || filterKo) && (
              <button
                onClick={() => {
                  setFilterStatus('');
                  setFilterType('');
                  setFilterCreator('');
                  setFilterKo('');
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                <X size={16} />
                Clear Filters
              </button>
            )}
          </>
        )}

        {viewMode === 'coldCalling' && (
          <>
            <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
              <select
                value={coldCallFilterStatus}
                onChange={(e) => setColdCallFilterStatus(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: coldCallFilterStatus ? '#fef3c7' : 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Statuses</option>
                {coldCallingStatusOptions.map((status) => (
                  <option key={getStatusOptionName(status)} value={getStatusOptionName(status)}>{getStatusOptionName(status)}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
              <select
                value={coldCallFilterType}
                onChange={(e) => setColdCallFilterType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: coldCallFilterType ? '#fef3c7' : 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">All Types</option>
                {coldCallingTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: '0 1 220px', minWidth: '160px', position: 'relative' }}>
              <input
                type="text"
                value={coldCallFilterKo}
                onChange={(e) => setColdCallFilterKo(e.target.value)}
                placeholder="Search KO or parcel ID"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: coldCallFilterKo ? '#fef3c7' : 'white',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {(coldCallFilterStatus || coldCallFilterType || coldCallFilterKo) && (
              <button
                onClick={() => {
                  setColdCallFilterStatus('');
                  setColdCallFilterType('');
                  setColdCallFilterKo('');
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                <X size={16} />
                Clear Filters
              </button>
            )}
          </>
        )}

        {/* Add Button */}
        <button 
          onClick={() => {
            setAddingToType(viewMode);
            if (viewMode === 'properties') {
              setNewProperty(createDefaultNewProperty());
            } else {
              setNewColdCall(createDefaultNewColdCall());
            }
            setShowAddModal(true);
          }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#d97706',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            whiteSpace: 'nowrap'
          }}
        >
          <Plus size={18} />
          Add New
        </button>

        {viewMode === 'properties' && (
          <button
            onClick={refreshZkDataForAllLeads}
            disabled={refreshingZk}
            style={{
              padding: '10px 20px',
              backgroundColor: refreshingZk ? '#94a3b8' : '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: refreshingZk ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}
          >
            <RefreshCcw size={18} />
            {refreshingZk ? 'Refreshing ZK...' : 'Refresh ZK data'}
          </button>
        )}

        {/* Export Buttons */}
        {viewMode === 'properties' && (
          <>
            <button 
              onClick={exportPropertiesToCSV}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}>
              <Download size={18} />
              Export CSV
            </button>
            <button 
              onClick={exportPropertiesToExcel}
              style={{
                padding: '10px 20px',
                backgroundColor: '#0891b2',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap'
              }}>
              <Download size={18} />
              Export Excel
            </button>
          </>
        )}
        {viewMode === 'coldCalling' && (
          <button 
            onClick={exportColdCallingToExcel}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0891b2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap'
            }}>
            <Download size={18} />
            Export Excel
          </button>
        )}
        </div>
      </div>

      {/* Interesting Properties Table */}
      {viewMode === 'properties' && (
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ 
            color: '#1e293b', 
            marginBottom: '15px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Building2 size={24} color="#d97706" />
            Interesting Properties
          </h2>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {/* Desktop Table */}
            {!isMobile && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={tableHeaderStyle}>Katastrska občina</th>
                    <th style={tableHeaderStyle}>ID parcele / stavbe</th>
                    <th style={tableHeaderStyle}>Type</th>
                    <th style={tableHeaderStyle}>Status</th>
                    <th style={tableHeaderStyle}>Owner Names (ZK)</th>
                    <th style={tableHeaderStyle}>ZK Date</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPropertiesLeads.map((lead, index) => (
                    <tr 
                      key={lead.id} 
                      onClick={async () => {
                        setSelectedLead(lead);
                        setShowDetailModal(true);
                        await fetchTickets(lead.id);
                      }}
                      style={{ 
                        borderBottom: '1px solid #f1f5f9',
                        backgroundColor: index % 2 === 0 ? 'white' : '#fafafa',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#fafafa'}
                    >
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: '500', color: '#1e293b' }}>{lead.katastrskaObcina}</div>
                        {lead.koName && (
                          <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{lead.koName}</div>
                        )}
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ fontWeight: '500', color: '#0891b2' }}>{lead.idParceleStavbe}</div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.type}</div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(lead.status, propertyStatusOptions),
                            display: 'inline-block'
                          }} />
                          <select
                            value={lead.status}
                            onChange={(e) => handlePropertyStatusChange(lead, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              padding: '6px 10px',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                              backgroundColor: '#f8fafc',
                              fontSize: '13px',
                              color: '#334155',
                              cursor: 'pointer'
                            }}
                          >
                            {buildStatusOptionsForLead(lead.status, propertyStatusOptions).map((status) => (
                              <option key={status.name} value={status.name}>{status.name}</option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.ownerNames}</div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ color: isZkDateStale(lead.zkDate) ? '#dc2626' : '#64748b', fontSize: '13px' }}>{lead.zkDate}</div>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                            handleDeleteProperty(lead.id);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#fee2e2',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#dc2626',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}

            {/* Mobile Cards View */}
            {isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px' }}>
                {filteredPropertiesLeads.map((lead) => (
                  <div 
                    key={lead.id}
                    onClick={async () => {
                      setSelectedLead(lead);
                      setShowDetailModal(true);
                      await fetchTickets(lead.id);
                    }}
                    style={{
                      padding: '15px',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    {/* Top Row: KO, ID, Type */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>KATASTRSKA OBČINA</div>
                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{lead.katastrskaObcina}</div>
                        {lead.koName && (
                          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{lead.koName}</div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>ID PARCELE / STAVBE</div>
                        <div style={{ fontWeight: '600', color: '#0891b2', fontSize: '14px' }}>{lead.idParceleStavbe}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>TYPE</div>
                        <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.type}</div>
                      </div>
                    </div>

                    {/* Bottom Row: Status and Owners */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '4px' }}>STATUS</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(lead.status, propertyStatusOptions),
                            display: 'inline-block'
                          }} />
                          <select
                            value={lead.status}
                            onChange={(e) => {
                              e.stopPropagation();
                              handlePropertyStatusChange(lead, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0',
                              backgroundColor: '#f8fafc',
                              fontSize: '12px',
                              color: '#334155',
                              cursor: 'pointer'
                            }}
                          >
                            {buildStatusOptionsForLead(lead.status, propertyStatusOptions).map((status) => (
                              <option key={status.name} value={status.name}>{status.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '4px' }}>OWNERS</div>
                        <div style={{ color: '#64748b', fontSize: '13px' }}>{lead.ownerNames}</div>
                      </div>
                    </div>

                    {/* Actions */}
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProperty(lead.id);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        backgroundColor: '#fee2e2',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#dc2626',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            {filteredPropertiesLeads.length === 0 && (
              <div style={{ 
                padding: '60px 20px', 
                textAlign: 'center', 
                color: '#94a3b8',
                fontSize: '16px'
              }}>
                No properties found matching your criteria.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cold Calling Table */}
      {viewMode === 'coldCalling' && (
        <div>
          <h2 style={{ 
            color: '#1e293b', 
            marginBottom: '15px', 
            fontSize: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Phone size={24} color="#d97706" />
            Cold Calling
          </h2>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {!isMobile && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={tableHeaderStyle}>Katastrska občina</th>
                    <th style={tableHeaderStyle}>ID parcele / stavbe</th>
                    <th style={tableHeaderStyle}>Kraj</th>
                    <th style={tableHeaderStyle}>Type</th>
                    <th style={tableHeaderStyle}>Status</th>
                    <th style={tableHeaderStyle}>Contact</th>
                    <th style={tableHeaderStyle}>Listing</th>
                    <th style={tableHeaderStyle}>Last Contact</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredColdCallingLeads.map((lead, index) => {
                    const isHighlighted = String(highlightedColdCallingLeadId) === String(lead.id);
                    const baseBackground = isHighlighted ? '#fff7ed' : index % 2 === 0 ? 'white' : '#fafafa';

                    return (
                      <tr
                        key={lead.id}
                        id={`cold-calling-lead-${lead.id}`}
                        onClick={() => openColdCallDetail(lead)}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          backgroundColor: baseBackground,
                          boxShadow: isHighlighted ? 'inset 0 0 0 2px #fb923c' : 'none',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isHighlighted ? '#fff7ed' : '#f8fafc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = baseBackground;
                        }}
                      >
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>{lead.katastrskaObcina || 'N/A'}</div>
                          {lead.koName && (
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{lead.koName}</div>
                          )}
                          {lead.naslov && (
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{lead.naslov}</div>
                          )}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: '500', color: '#0891b2' }}>{lead.idParceleStavbe || 'N/A'}</div>
                          {lead.idStavbe && (
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>ID stavbe: {lead.idStavbe}</div>
                          )}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ color: '#1e293b', fontSize: '14px' }}>{lead.kraj || 'N/A'}</div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.type || 'N/A'}</div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(lead.status, coldCallingStatusOptions),
                              display: 'inline-block'
                            }} />
                            <select
                              value={lead.status}
                              onChange={(e) => handleColdCallStatusChange(lead, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc',
                                fontSize: '13px',
                                color: '#334155',
                                cursor: 'pointer'
                              }}
                            >
                              {buildStatusOptionsForLead(lead.status, coldCallingStatusOptions).map((status) => (
                                <option key={status.name} value={status.name}>{status.name}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ fontWeight: '500', color: '#1e293b' }}>{lead.name || 'N/A'}</div>
                          <div style={{ color: '#64748b', fontSize: '13px', marginTop: '2px' }}>{lead.phone}</div>
                          {lead.email && (
                            <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{lead.email}</div>
                          )}
                        </td>
                        <td style={tableCellStyle}>
                          {lead.listingLink ? (
                            <a
                              href={lead.listingLink}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#0891b2', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
                            >
                              Open listing
                            </a>
                          ) : (
                            <div style={{ color: '#94a3b8', fontSize: '13px' }}>N/A</div>
                          )}
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ color: '#64748b', fontSize: '13px' }}>{formatDisplayDate(lead.lastContact)}</div>
                        </td>
                        <td style={tableCellStyle}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openColdCallDetail(lead);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#f1f5f9',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: '#475569',
                                fontWeight: '500'
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteColdCall(lead.id);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#fee2e2',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: '#dc2626',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px' }}>
                {filteredColdCallingLeads.map((lead) => {
                  const isHighlighted = String(highlightedColdCallingLeadId) === String(lead.id);

                  return (
                    <div
                      key={lead.id}
                      id={`cold-calling-lead-${lead.id}`}
                      onClick={() => openColdCallDetail(lead)}
                      style={{
                        padding: '15px',
                        backgroundColor: isHighlighted ? '#fff7ed' : 'white',
                        border: isHighlighted ? '2px solid #fb923c' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '8px', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>KATASTRSKA OBČINA</div>
                          <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{lead.katastrskaObcina || 'N/A'}</div>
                          {lead.koName && (
                            <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{lead.koName}</div>
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>ID PARCELE / STAVBE</div>
                          <div style={{ fontWeight: '600', color: '#0891b2', fontSize: '14px' }}>{lead.idParceleStavbe || 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>KRAJ</div>
                          <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.kraj || 'N/A'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '2px' }}>TYPE</div>
                          <div style={{ color: '#64748b', fontSize: '14px' }}>{lead.type || 'N/A'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '4px' }}>STATUS</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: getStatusColor(lead.status, coldCallingStatusOptions),
                              display: 'inline-block'
                            }} />
                            <select
                              value={lead.status}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleColdCallStatusChange(lead, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc',
                                fontSize: '12px',
                                color: '#334155',
                                cursor: 'pointer'
                              }}
                            >
                              {buildStatusOptionsForLead(lead.status, coldCallingStatusOptions).map((status) => (
                                <option key={status.name} value={status.name}>{status.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', marginBottom: '4px' }}>CONTACT</div>
                          <div style={{ color: '#64748b', fontSize: '13px' }}>{lead.name || 'N/A'}</div>
                          <div style={{ color: '#1e293b', fontSize: '13px', fontWeight: '600', marginTop: '2px' }}>{lead.phone}</div>
                        </div>
                      </div>

                      {lead.listingLink && (
                        <div style={{ marginBottom: '12px' }}>
                          <a
                            href={lead.listingLink}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#0891b2', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
                          >
                            Open listing
                          </a>
                        </div>
                      )}

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteColdCall(lead.id);
                        }}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          backgroundColor: '#fee2e2',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#dc2626',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {filteredColdCallingLeads.length === 0 && (
              <div style={{ 
                padding: '60px 20px', 
                textAlign: 'center', 
                color: '#94a3b8',
                fontSize: '16px'
              }}>
                No cold calling leads found matching your criteria.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#1e293b', margin: 0 }}>
                {addingToType === 'properties' ? 'Add Interesting Property' : 'Add Cold Call Lead'}
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '5px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Property Form */}
            {addingToType === 'properties' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Katastrska občina *
                  </label>
                  <input
                    type="text"
                    value={newProperty.katastrskaObcina}
                    onChange={(e) => setNewProperty({...newProperty, katastrskaObcina: e.target.value})}
                    style={inputStyle}
                    placeholder="e.g., Ljubljana"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    ID parcele *
                  </label>
                  <input
                    type="text"
                    value={newProperty.idParceleStavbe}
                    onChange={(e) => setNewProperty({...newProperty, idParceleStavbe: e.target.value})}
                    style={inputStyle}
                    placeholder="e.g., 1234/5"
                  />
                </div>

                {isHouseType(newProperty.type) && (
                  <>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                        ID stavbe
                      </label>
                      <input
                        type="text"
                        value={newProperty.idStavbe}
                        onChange={(e) => setNewProperty({...newProperty, idStavbe: e.target.value})}
                        style={inputStyle}
                        placeholder="e.g., 456"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                        Naslov
                      </label>
                      <input
                        type="text"
                        value={newProperty.naslov}
                        onChange={(e) => setNewProperty({...newProperty, naslov: e.target.value})}
                        style={inputStyle}
                        placeholder="e.g., Dunajska cesta 10, Ljubljana"
                      />
                    </div>
                  </>
                )}

                <button
                  onClick={fetchZKData}
                  disabled={zkLoading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#0891b2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: zkLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: zkLoading ? 0.6 : 1
                  }}
                >
                  {zkLoading ? 'Fetching ZK data...' : 'Preview ZK Data'}
                </button>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Type *
                  </label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                      value={newProperty.type}
                      onChange={(e) => setNewProperty({...newProperty, type: e.target.value})}
                      style={{ ...inputStyle, flex: 1 }}
                    >
                      {propertyTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowTypeManager(true)}
                      style={{
                        padding: '10px 15px',
                        backgroundColor: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#475569'
                      }}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Status *
                  </label>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <select
                      value={newProperty.status}
                      onChange={(e) => setNewProperty({...newProperty, status: e.target.value, customStatus: ''})}
                      style={{ ...inputStyle, flex: 1 }}
                    >
                      {propertyStatusOptions.map(status => (
                        <option key={getStatusOptionName(status)} value={getStatusOptionName(status)}>{getStatusOptionName(status)}</option>
                      ))}
                      <option value="custom">Custom...</option>
                    </select>
                    <button
                      onClick={() => setShowStatusManager(true)}
                      style={{
                        padding: '10px 15px',
                        backgroundColor: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#475569'
                      }}
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                  {newProperty.status === 'custom' && (
                    <input
                      type="text"
                      value={newProperty.customStatus}
                      onChange={(e) => setNewProperty({...newProperty, customStatus: e.target.value})}
                      style={inputStyle}
                      placeholder="Enter custom status"
                    />
                  )}
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    onClick={handleAddProperty}
                    disabled={zkLoading}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#d97706',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: zkLoading ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      opacity: zkLoading ? 0.6 : 1
                    }}
                  >
                    Add Property
                  </button>
                  <button
                    onClick={() => setShowAddModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Cold Call Form */}
            {addingToType === 'coldCalling' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Type
                  </label>
                  <select
                    value={newColdCall.type}
                    onChange={(e) => setNewColdCall({ ...newColdCall, type: e.target.value })}
                    style={inputStyle}
                  >
                    {coldCallingTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={newColdCall.phone}
                    onChange={(e) => setNewColdCall({ ...newColdCall, phone: e.target.value })}
                    style={inputStyle}
                    placeholder="+386 XX XXX XXX"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Listing link
                  </label>
                  <input
                    type="url"
                    value={newColdCall.listingLink}
                    onChange={(e) => setNewColdCall({ ...newColdCall, listingLink: e.target.value })}
                    style={inputStyle}
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontWeight: '500', fontSize: '14px' }}>
                    Kraj
                  </label>
                  <input
                    type="text"
                    value={newColdCall.kraj || ''}
                    onChange={(e) => setNewColdCall({ ...newColdCall, kraj: e.target.value })}
                    style={inputStyle}
                    placeholder="Optional"
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    onClick={handleAddColdCall}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#d97706',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Add Lead
                  </button>
                  <button
                    onClick={() => setShowAddModal(false)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showColdCallDetailModal && selectedColdCall && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: isMobile ? '10px' : '20px'
          }}
          onClick={() => {
            setShowColdCallDetailModal(false);
            setSelectedColdCall(null);
            setIsEditingColdCallDetails(false);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '15px' : '30px',
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%',
              maxHeight: isMobile ? '100vh' : '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: isMobile ? '15px' : '25px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: isMobile ? '10px' : '0'
            }}>
              <h2 style={{
                color: '#1e293b',
                margin: 0,
                fontSize: isMobile ? '18px' : '24px',
                width: isMobile ? '100%' : 'auto'
              }}>Cold Call Details</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={handleToggleColdCallEdit}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isEditingColdCallDetails ? '#10b981' : '#d97706',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {isEditingColdCallDetails ? 'Done Editing' : 'Edit'}
                </button>
                <button
                  onClick={() => {
                    setShowColdCallDetailModal(false);
                    setSelectedColdCall(null);
                    setIsEditingColdCallDetails(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '5px'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '25px', padding: isMobile ? '12px' : '20px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '12px' }}>
              <h3 style={{ color: '#1e293b', marginBottom: isMobile ? '10px' : '15px', fontSize: isMobile ? '16px' : '18px' }}>Property Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '15px' }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Katastrska občina</div>
                  {isEditingColdCallDetails ? (
                    <input type="text" value={selectedColdCall.katastrskaObcina || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, katastrskaObcina: e.target.value })} style={inputStyle} placeholder="Optional" />
                  ) : (
                    <>
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.katastrskaObcina || 'N/A'}</div>
                      {selectedColdCall.koName && (
                        <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{selectedColdCall.koName}</div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ID parcele / stavbe</div>
                  {isEditingColdCallDetails ? (
                    <input type="text" value={selectedColdCall.idParceleStavbe || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, idParceleStavbe: e.target.value })} style={inputStyle} placeholder="Optional" />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <div style={{ color: '#0891b2', fontWeight: '500' }}>{selectedColdCall.idParceleStavbe || 'N/A'}</div>
                      <button
                        onClick={() => handleCopyKoId(selectedColdCall)}
                        style={{
                          padding: '6px 10px',
                          backgroundColor: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: '#334155',
                          fontWeight: '500'
                        }}
                      >
                        Copy ID
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Kraj</div>
                  {isEditingColdCallDetails ? (
                    <input type="text" value={selectedColdCall.kraj || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, kraj: e.target.value })} style={inputStyle} placeholder="Optional" />
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.kraj || 'N/A'}</div>
                  )}
                </div>
                {(isHouseType(selectedColdCall.type) || selectedColdCall.idStavbe) && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ID stavbe</div>
                    {isEditingColdCallDetails ? (
                      <input type="text" value={selectedColdCall.idStavbe || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, idStavbe: e.target.value })} style={inputStyle} placeholder="Optional" />
                    ) : (
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.idStavbe || 'N/A'}</div>
                    )}
                  </div>
                )}
                {(isHouseType(selectedColdCall.type) || selectedColdCall.naslov) && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Naslov</div>
                    {isEditingColdCallDetails ? (
                      <input type="text" value={selectedColdCall.naslov || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, naslov: e.target.value })} style={inputStyle} placeholder="Optional" />
                    ) : (
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.naslov || 'N/A'}</div>
                    )}
                  </div>
                )}
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Type</div>
                  {isEditingColdCallDetails ? (
                    <select value={selectedColdCall.type} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, type: e.target.value })} style={inputStyle}>
                      {(coldCallingTypes.includes(selectedColdCall.type)
                        ? coldCallingTypes
                        : [...coldCallingTypes, selectedColdCall.type].filter(Boolean)
                      ).map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.type || 'N/A'}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Status</div>
                  {isEditingColdCallDetails ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select value={selectedColdCall.status} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, status: e.target.value })} style={{ ...inputStyle, flex: 1 }}>
                        {buildStatusOptionsForLead(selectedColdCall.status, coldCallingStatusOptions).map((status) => (
                          <option key={status.name} value={status.name}>{status.name}</option>
                        ))}
                      </select>
                      <button onClick={() => setShowStatusManager(true)} style={{ padding: '8px 12px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#475569' }}>
                        <Plus size={16} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: getStatusColor(selectedColdCall.status, coldCallingStatusOptions), display: 'inline-block' }} />
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.status || 'N/A'}</span>
                    </div>
                  )}
                </div>
                <div style={{ gridColumn: isMobile ? '1' : 'span 2' }}>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ZK data</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ color: isZkDateStale(selectedColdCall.zkDate) ? '#dc2626' : '#1e293b', fontWeight: '500' }}>{selectedColdCall.zkDate || 'N/A'}</div>
                    <button onClick={handleFetchColdCallZkData} disabled={zkLoading} style={{ padding: '6px 12px', backgroundColor: '#0891b2', color: 'white', border: 'none', borderRadius: '6px', cursor: zkLoading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '500' }}>
                      {zkLoading ? 'Fetching...' : 'Add ZK data'}
                    </button>
                    {selectedColdCall.zkExtractId && (
                      <>
                        <button onClick={handleDownloadColdCallZKExtract} style={{ padding: '6px 12px', backgroundColor: '#0891b2', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Download size={14} />
                          PDF
                        </button>
                        <button onClick={handleDownloadColdCallZKHistory} style={{ padding: '6px 12px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Download size={14} />
                          History
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {supportsPastSales(selectedColdCall.type) && (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px', flexDirection: isMobile ? 'column' : 'row', marginBottom: isMobile ? '10px' : '15px' }}>
                  <h3 style={{ color: '#1e293b', marginBottom: 0, fontSize: isMobile ? '16px' : '18px' }}>Past sales</h3>
                  <button onClick={handleRefreshColdCallPastSales} disabled={coldCallPastSalesLoading || refreshingColdCallPastSales} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #fdba74', backgroundColor: coldCallPastSalesLoading || refreshingColdCallPastSales ? '#fed7aa' : '#fff7ed', color: '#9a3412', cursor: coldCallPastSalesLoading || refreshingColdCallPastSales ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
                    <RefreshCcw size={14} />
                    {refreshingColdCallPastSales ? 'Refreshing...' : 'Refresh past sales'}
                  </button>
                </div>
                {coldCallPastSalesLoading ? (
                  <div style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>Loading past sales...</div>
                ) : coldCallPastSales.length === 0 ? (
                  <div style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>No past sales found for this property.</div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {coldCallPastSales.map((sale, index) => (
                      <div key={`${sale.transactionId || 'sale'}-${index}`} style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#fff7ed', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #fdba74' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '160px 160px 1fr', gap: '10px' }}>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Sale date</div>
                            <div style={{ color: '#7c2d12', fontWeight: '600' }}>{formatDisplayDate(sale.saleDate)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Price</div>
                            <div style={{ color: '#7c2d12', fontWeight: '600' }}>{formatDisplayPrice(sale.price)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Included parcels</div>
                            <div style={{ color: '#7c2d12', whiteSpace: 'pre-wrap' }}>{Array.isArray(sale.includedParcels) && sale.includedParcels.length > 0 ? sale.includedParcels.join(' • ') : 'N/A'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedColdCall.owners && selectedColdCall.owners.length > 0 && (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <h3 style={{ color: '#1e293b', marginBottom: isMobile ? '10px' : '15px', fontSize: isMobile ? '16px' : '18px' }}>Owners</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {selectedColdCall.owners.map((owner, index) => (
                    <div key={`${owner.name || 'owner'}-${index}`} style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0' }}>
                      <div style={{ color: '#1e293b', fontWeight: '600' }}>{owner.name || 'N/A'}</div>
                      <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>{[owner.share ? `Share: ${owner.share}` : '', owner.type ? `Type: ${owner.type}` : ''].filter(Boolean).join(' • ') || 'No ownership metadata'}</div>
                      {owner.dateOfBirth && <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Born: {owner.dateOfBirth}</div>}
                      {owner.address && <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Address: {owner.address}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: isMobile ? '15px' : '25px', padding: isMobile ? '12px' : '20px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '12px' }}>
              <h3 style={{ color: '#1e293b', marginBottom: isMobile ? '10px' : '15px', fontSize: isMobile ? '16px' : '18px' }}>Contact Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '15px' }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Owner / Contact name</div>
                  {isEditingColdCallDetails ? (
                    <input type="text" value={selectedColdCall.name || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, name: e.target.value })} style={inputStyle} placeholder="Optional" />
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.name || 'N/A'}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Phone</div>
                  {isEditingColdCallDetails ? (
                    <input type="tel" value={selectedColdCall.phone || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, phone: e.target.value })} style={inputStyle} placeholder="Required" />
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.phone || 'N/A'}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Email</div>
                  {isEditingColdCallDetails ? (
                    <input type="email" value={selectedColdCall.email || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, email: e.target.value })} style={inputStyle} placeholder="Optional" />
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedColdCall.email || 'N/A'}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Last contact</div>
                  {isEditingColdCallDetails ? (
                    <input type="date" value={selectedColdCall.lastContact || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, lastContact: e.target.value })} style={inputStyle} />
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{formatDisplayDate(selectedColdCall.lastContact)}</div>
                  )}
                </div>
                <div style={{ gridColumn: isMobile ? '1' : 'span 2' }}>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Listing link</div>
                  {isEditingColdCallDetails ? (
                    <input type="url" value={selectedColdCall.listingLink || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, listingLink: e.target.value })} style={inputStyle} placeholder="https://..." />
                  ) : selectedColdCall.listingLink ? (
                    <a href={selectedColdCall.listingLink} target="_blank" rel="noreferrer" style={{ color: '#0891b2', fontWeight: '500', textDecoration: 'none', wordBreak: 'break-all' }}>
                      {selectedColdCall.listingLink}
                    </a>
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>N/A</div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '10px' : '15px', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '10px' : '0' }}>
                <h3 style={{ color: '#1e293b', margin: 0, fontSize: isMobile ? '16px' : '18px', width: isMobile ? '100%' : 'auto' }}>Interactions & Calls</h3>
              </div>
              <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>Type of Interaction</label>
                    <select value={newColdCallInteractionType} onChange={(e) => setNewColdCallInteractionType(e.target.value)} style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none', backgroundColor: 'white' }}>
                      <option value="Call">Call</option>
                      <option value="In Person Talk">In Person Talk</option>
                      <option value="Email">Email</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '6px', fontWeight: '500' }}>Notes</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" value={newColdCallInteractionNotes} onChange={(e) => setNewColdCallInteractionNotes(e.target.value)} onKeyPress={async (e) => {
                        if (e.key === 'Enter' && newColdCallInteractionNotes.trim()) {
                          try {
                            const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/tickets`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ ticketType: 'interaction', noteContent: `${newColdCallInteractionType}: ${newColdCallInteractionNotes}`, userName: localStorage.getItem('leadGenUser') || 'User' })
                            });
                            if (response.ok) {
                              setNewColdCallInteractionNotes('');
                              await fetchColdCallTickets(selectedColdCall.id);
                            }
                          } catch (error) {
                            console.error('Error adding cold call interaction:', error);
                          }
                        }
                      }} style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' }} placeholder="Describe the interaction..." />
                      <button onClick={async () => {
                        if (!newColdCallInteractionNotes.trim()) return;
                        try {
                          const response = await authFetch(`${API_BASE}/api/leads/cold-calling/${selectedColdCall.id}/tickets`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ ticketType: 'interaction', noteContent: `${newColdCallInteractionType}: ${newColdCallInteractionNotes}`, userName: localStorage.getItem('leadGenUser') || 'User' })
                          });
                          if (response.ok) {
                            setNewColdCallInteractionNotes('');
                            await fetchColdCallTickets(selectedColdCall.id);
                          }
                        } catch (error) {
                          console.error('Error adding cold call interaction:', error);
                          alert('Failed to add interaction.');
                        }
                      }} disabled={!newColdCallInteractionNotes.trim()} style={{ padding: '10px 20px', backgroundColor: newColdCallInteractionNotes.trim() ? '#0891b2' : '#e2e8f0', color: newColdCallInteractionNotes.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '6px', cursor: newColdCallInteractionNotes.trim() ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                        <Plus size={16} />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '10px' : '15px', flexWrap: 'wrap' }}>
                <h3 style={{ color: '#1e293b', margin: 0, fontSize: isMobile ? '16px' : '18px' }}>General Notes</h3>
                <button onClick={() => setShowColdCallChecklistModal(true)} style={{ padding: '8px 12px', backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <CheckSquare size={15} /> Checklist
                </button>
              </div>
              <textarea value={selectedColdCall.notes || ''} onChange={(e) => setSelectedColdCall({ ...selectedColdCall, notes: e.target.value })} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', minHeight: '150px', resize: 'vertical', boxSizing: 'border-box' }} placeholder="Add general notes about this cold call lead..." />
            </div>

            {filledColdCallChecklistItems.length > 0 && (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <h3 style={{ color: '#1e293b', marginBottom: isMobile ? '10px' : '15px', fontSize: isMobile ? '16px' : '18px' }}>Checklist</h3>
                <div style={{ display: 'grid', gap: '10px' }}>
                  {filledColdCallChecklistItems.map(({ item, value }) => (
                    <div key={item.id} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                      {item.type === 'checkbox' ? (
                        <div style={{ color: '#16a34a', fontSize: '18px', fontWeight: '700', lineHeight: 1 }}>✓</div>
                      ) : item.type === 'document' ? (
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {getChecklistDocumentValues(value).map((documentValue, index) => (
                            <button key={`${documentValue.documentPath}-${index}`} onClick={() => handleDocumentDownload(documentValue.documentPath, documentValue.documentName)} style={{ padding: '6px 12px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>
                              <span>⬇</span>
                              {documentValue.documentName || 'Download'}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#1e293b', fontWeight: '500', whiteSpace: 'pre-wrap' }}>{formatChecklistValue(item, value)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h3 style={{ color: '#1e293b', marginBottom: isMobile ? '10px' : '15px', fontSize: isMobile ? '16px' : '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>📋 Activity History (Tickets)</h3>
              <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Add Note</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" value={newColdCallTicketNote} onChange={(e) => setNewColdCallTicketNote(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddColdCallNoteTicket()} style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none' }} placeholder="Type a note and press Enter or click Add..." />
                  <button onClick={handleAddColdCallNoteTicket} disabled={!newColdCallTicketNote.trim()} style={{ padding: '10px 20px', backgroundColor: newColdCallTicketNote.trim() ? '#3b82f6' : '#e2e8f0', color: newColdCallTicketNote.trim() ? 'white' : '#94a3b8', border: 'none', borderRadius: '6px', cursor: newColdCallTicketNote.trim() ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Upload Document</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="file" onChange={handleColdCallDocumentUpload} disabled={uploadingColdCallDocument} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', backgroundColor: 'white' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar" />
                  {uploadingColdCallDocument && <span style={{ color: '#8b5cf6', fontSize: '13px' }}>Uploading...</span>}
                </div>
              </div>

              <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {coldCallTickets.length > 0 ? (
                  coldCallTickets.map((ticket) => {
                    const formatted = formatTicket(ticket);
                    return (
                      <div key={ticket.id} style={{ padding: '12px 15px', backgroundColor: 'white', border: `1px solid ${formatted.color}30`, borderLeft: `4px solid ${formatted.color}`, borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span style={{ fontSize: '18px' }}>{formatted.icon}</span>
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{formatted.title}</span>
                          </div>
                          <span style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>{new Date(ticket.createdAt).toLocaleString()}</span>
                        </div>
                        <div style={{ color: '#475569', fontSize: '13px', marginLeft: '26px' }}>{formatted.content}</div>
                        {ticket.ticketType === 'document' && ticket.documentName && (
                          <button onClick={() => handleDocumentDownload(ticket.documentPath, ticket.documentName)} style={{ marginLeft: '26px', marginTop: '8px', padding: '6px 12px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <span>⬇</span>
                            Download
                          </button>
                        )}
                        {ticket.userName && <div style={{ color: '#94a3b8', fontSize: '11px', marginLeft: '26px', marginTop: '4px' }}>by {ticket.userName}</div>}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: '40px 20px', textAlign: 'center', color: '#94a3b8', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px dashed #e2e8f0' }}>
                    No activity history yet. Add a note above or make changes to start tracking.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <UniversalChecklistModal
        key={`cold-call-checklist-${selectedColdCall?.id || 'none'}-${selectedColdCall?.type || 'none'}`}
        isOpen={showColdCallChecklistModal && Boolean(selectedColdCall)}
        title={selectedColdCall ? `${selectedColdCall.katastrskaObcina || 'Cold Call'} ${selectedColdCall.idParceleStavbe || selectedColdCall.phone || ''}`.trim() : 'Checklist'}
        typeLabel={selectedColdCall?.type || ''}
        items={coldCallChecklistTemplateItems}
        values={coldCallChecklistValues}
        loading={coldCallChecklistLoading}
        saving={coldCallChecklistSaving}
        onClose={() => setShowColdCallChecklistModal(false)}
        onSaveValues={handleSaveColdCallChecklistValues}
        onSaveTemplate={handleSaveColdCallChecklistTemplate}
        onUploadDocument={handleChecklistDocumentUpload}
        onDownloadDocument={(value) => handleDocumentDownload(value?.documentPath, value?.documentName)}
      />

      <UniversalChecklistModal
        key={`property-checklist-${selectedLead?.id || 'none'}-${selectedLead?.type || 'none'}`}
        isOpen={showChecklistModal && Boolean(selectedLead)}
        title={selectedLead ? `${selectedLead.katastrskaObcina} ${selectedLead.idParceleStavbe}` : 'Checklist'}
        typeLabel={selectedLead?.type || ''}
        items={checklistTemplateItems}
        values={checklistValues}
        loading={checklistLoading}
        saving={checklistSaving}
        onClose={() => setShowChecklistModal(false)}
        onSaveValues={handleSaveChecklistValues}
        onSaveTemplate={handleSaveChecklistTemplate}
        onUploadDocument={handleChecklistDocumentUpload}
        onDownloadDocument={(value) => handleDocumentDownload(value?.documentPath, value?.documentName)}
      />

      {/* Property Type Manager Modal */}
      {showTypeManager && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#1e293b', marginBottom: '15px' }}>Add Property Type</h3>
            <input
              type="text"
              value={newPropertyType}
              onChange={(e) => setNewPropertyType(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddPropertyType()}
              style={inputStyle}
              placeholder="Enter new property type"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button
                onClick={handleAddPropertyType}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowTypeManager(false);
                  setNewPropertyType('');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Manager Modal */}
      {showStatusManager && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1002
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#1e293b', marginBottom: '15px' }}>Manage Status Options</h3>
            
            {/* List of existing statuses */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#64748b', fontSize: '13px', marginBottom: '10px', textTransform: 'uppercase' }}>
                Existing Statuses
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {managedStatusOptions.map((status, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '8px 12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0'
                  }}>
                    {editingStatusIndex === index ? (
                      <>
                        <input
                          type="text"
                          value={editingStatusValue}
                          onChange={(e) => setEditingStatusValue(e.target.value)}
                          onKeyPress={async (e) => {
                            if (e.key === 'Enter') {
                              try {
                                const response = await authFetch(`${managedStatusEndpoint}/${encodeURIComponent(getStatusOptionName(status))}`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ newName: editingStatusValue, color: editingStatusColor })
                                });
                                
                                if (response.ok) {
                                  const updatedStatus = normalizeStatusOption(await response.json());
                                  const updated = [...managedStatusOptions];
                                  updated[index] = updatedStatus;
                                  updateManagedStatusOptions(updated);
                                  setEditingStatusIndex(null);
                                  setEditingStatusValue('');
                                  setEditingStatusColor('#64748b');
                                  await refreshManagedLeadList();
                                } else {
                                  const error = await response.json();
                                  alert(error.error || 'Failed to update status');
                                }
                              } catch (error) {
                                console.error('Error updating status:', error);
                                alert('Error updating status: ' + error.message);
                              }
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            border: '1px solid #d97706',
                            borderRadius: '4px',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                          autoFocus
                        />
                        <input
                          type="color"
                          value={editingStatusColor}
                          onChange={(e) => setEditingStatusColor(e.target.value)}
                          style={{ width: '42px', height: '34px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                          title="Status color"
                        />
                        <button
                          onClick={async () => {
                            try {
                              const response = await authFetch(`${managedStatusEndpoint}/${encodeURIComponent(getStatusOptionName(status))}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ newName: editingStatusValue, color: editingStatusColor })
                              });
                              
                              if (response.ok) {
                                const updatedStatus = normalizeStatusOption(await response.json());
                                const updated = [...managedStatusOptions];
                                updated[index] = updatedStatus;
                                updateManagedStatusOptions(updated);
                                setEditingStatusIndex(null);
                                setEditingStatusValue('');
                                setEditingStatusColor('#64748b');
                                await refreshManagedLeadList();
                              } else {
                                const error = await response.json();
                                alert(error.error || 'Failed to update status');
                              }
                            } catch (error) {
                              console.error('Error updating status:', error);
                              alert('Error updating status: ' + error.message);
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingStatusIndex(null);
                            setEditingStatusValue('');
                            setEditingStatusColor('#64748b');
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: getStatusOptionColor(status), display: 'inline-block' }} />
                        <span style={{ flex: 1, color: '#1e293b', fontSize: '14px' }}>{getStatusOptionName(status)}</span>
                        <button
                          onClick={() => {
                            setEditingStatusIndex(index);
                            setEditingStatusValue(getStatusOptionName(status));
                            setEditingStatusColor(getStatusOptionColor(status));
                          }}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete the status "${getStatusOptionName(status)}"?`)) {
                              try {
                                const response = await authFetch(`${managedStatusEndpoint}/${encodeURIComponent(getStatusOptionName(status))}`, {
                                  method: 'DELETE'
                                });
                                
                                if (response.ok) {
                                  updateManagedStatusOptions(managedStatusOptions.filter((_, i) => i !== index));
                                } else {
                                  const error = await response.json();
                                  alert(error.error || 'Failed to delete status');
                                }
                              } catch (error) {
                                console.error('Error deleting status:', error);
                                alert('Error deleting status: ' + error.message);
                              }
                            }
                          }}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Add new status */}
            <div>
              <h4 style={{ color: '#64748b', fontSize: '13px', marginBottom: '10px', textTransform: 'uppercase' }}>
                Add New Status
              </h4>
              <input
                type="text"
                value={newStatusOption}
                onChange={(e) => setNewStatusOption(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddStatusOption()}
                style={inputStyle}
                placeholder="Enter new status"
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                <input
                  type="color"
                  value={newStatusOptionColor}
                  onChange={(e) => setNewStatusOptionColor(e.target.value)}
                  style={{ width: '44px', height: '36px', border: 'none', background: 'transparent', cursor: 'pointer' }}
                  title="New status color"
                />
                <span style={{ color: '#64748b', fontSize: '13px' }}>Choose status color</span>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button
                onClick={handleAddStatusOption}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowStatusManager(false);
                  setNewStatusOption('');
                  setNewStatusOptionColor('#64748b');
                  setEditingStatusIndex(null);
                  setEditingStatusValue('');
                  setEditingStatusColor('#64748b');
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal for Properties */}
      {showDetailModal && selectedLead && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: isMobile ? '10px' : '20px'
        }}
        onClick={() => {
          setShowDetailModal(false);
          setTickets([]);
          setNewTicketNote('');
          setHasAddedPropertyChecklistSection(false);
          setHasExistingLeadMptChecklist(false);
          setHasAddedLeadMptChecklist(false);
        }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              padding: isMobile ? '15px' : '30px',
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%',
              maxHeight: isMobile ? '100vh' : '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
            }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: isMobile ? '15px' : '25px',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: isMobile ? '10px' : '0'
            }}>
              <h2 style={{ 
                color: '#1e293b', 
                margin: 0,
                fontSize: isMobile ? '18px' : '24px',
                width: isMobile ? '100%' : 'auto'
              }}>Property Details</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={handleToggleEdit}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isEditingDetails ? '#10b981' : '#d97706',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  {isEditingDetails ? 'Done Editing' : 'Edit'}
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setIsEditingDetails(false);
                    setShowChecklistModal(false);
                    setTickets([]);
                    setNewTicketNote('');
                    setNewInteractionNotes('');
                    setHasAddedPropertyChecklistSection(false);
                    setHasExistingLeadMptChecklist(false);
                    setHasAddedLeadMptChecklist(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '5px'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Property Info Section */}
            <div style={{ 
              marginBottom: isMobile ? '15px' : '25px', 
              padding: isMobile ? '12px' : '20px', 
              backgroundColor: '#f8fafc', 
              borderRadius: isMobile ? '8px' : '12px' 
            }}>
              <h3 style={{ 
                color: '#1e293b', 
                marginBottom: isMobile ? '10px' : '15px', 
                fontSize: isMobile ? '16px' : '18px' 
              }}>Property Information</h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
                gap: isMobile ? '12px' : '15px' 
              }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Katastrska občina</div>
                  <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedLead.katastrskaObcina}</div>
                  {selectedLead.koName && (
                    <div style={{ color: '#64748b', fontSize: '12px', marginTop: '2px' }}>{selectedLead.koName}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ID parcele</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ color: '#0891b2', fontWeight: '500' }}>{selectedLead.idParceleStavbe}</div>
                    <button
                      onClick={() => handleCopyKoId(selectedLead)}
                      style={{
                        padding: '6px 10px',
                        backgroundColor: '#f1f5f9',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        color: '#334155',
                        fontWeight: '500'
                      }}
                    >
                      Copy ID
                    </button>
                  </div>
                  {isEditingDetails && (
                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '6px' }}>
                      Parcel ID is fixed after the lead is created.
                    </div>
                  )}
                </div>
                {(isHouseType(selectedLead.type) || selectedLead.idStavbe) && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ID stavbe</div>
                    {isEditingDetails ? (
                      <input
                        type="text"
                        value={selectedLead.idStavbe || ''}
                        onChange={(e) => setSelectedLead({ ...selectedLead, idStavbe: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontSize: '13px',
                          color: '#334155'
                        }}
                        placeholder="Optional"
                      />
                    ) : (
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedLead.idStavbe || 'N/A'}</div>
                    )}
                  </div>
                )}
                {(isHouseType(selectedLead.type) || selectedLead.naslov) && (
                  <div>
                    <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Naslov</div>
                    {isEditingDetails ? (
                      <input
                        type="text"
                        value={selectedLead.naslov || ''}
                        onChange={(e) => setSelectedLead({ ...selectedLead, naslov: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontSize: '13px',
                          color: '#334155'
                        }}
                        placeholder="Optional"
                      />
                    ) : (
                      <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedLead.naslov || 'N/A'}</div>
                    )}
                  </div>
                )}
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Type</div>
                  {isEditingDetails ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={selectedLead.type}
                        onChange={(e) => setSelectedLead({ ...selectedLead, type: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontSize: '13px',
                          color: '#334155'
                        }}
                      >
                        {propertyTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowTypeManager(true)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: '#475569'
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ color: '#1e293b', fontWeight: '500' }}>{selectedLead.type}</div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>Status</div>
                  {isEditingDetails ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        value={selectedLead.status}
                        onChange={(e) => setSelectedLead({ ...selectedLead, status: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0',
                          backgroundColor: '#f8fafc',
                          fontSize: '13px',
                          color: '#334155'
                        }}
                      >
                        {buildStatusOptionsForLead(selectedLead.status, propertyStatusOptions).map((status) => (
                          <option key={status.name} value={status.name}>{status.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowStatusManager(true)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: '#f1f5f9',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          color: '#475569'
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(selectedLead.status, propertyStatusOptions),
                        display: 'inline-block'
                      }} />
                      <span style={{ color: '#1e293b', fontWeight: '500' }}>{selectedLead.status}</span>
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '5px' }}>ZK Date</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ color: isZkDateStale(selectedLead.zkDate) ? '#dc2626' : '#1e293b', fontWeight: '500' }}>{selectedLead.zkDate}</div>
                    {selectedLead.zkExtractId && (
                      <>
                        <button
                          onClick={handleDownloadZKExtract}
                          title="Download ZK Extract"
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#0891b2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Download size={14} />
                          PDF
                        </button>
                        <button
                          onClick={handleDownloadZKHistory}
                          title="Download ZK History"
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#7c3aed',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Download size={14} />
                          History
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {supportsPastSales(selectedLead.type) && (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: '12px',
                  flexDirection: isMobile ? 'column' : 'row',
                  marginBottom: isMobile ? '10px' : '15px'
                }}>
                  <h3 style={{
                    color: '#1e293b',
                    marginBottom: 0,
                    fontSize: isMobile ? '16px' : '18px'
                  }}>Past sales</h3>
                  <button
                    onClick={handleRefreshPastSales}
                    disabled={pastSalesLoading || refreshingPastSales}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #fdba74',
                      backgroundColor: pastSalesLoading || refreshingPastSales ? '#fed7aa' : '#fff7ed',
                      color: '#9a3412',
                      cursor: pastSalesLoading || refreshingPastSales ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    <RefreshCcw size={14} />
                    {refreshingPastSales ? 'Refreshing...' : 'Refresh past sales'}
                  </button>
                </div>
                {pastSalesLoading ? (
                  <div style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>
                    Loading past sales...
                  </div>
                ) : pastSales.length === 0 ? (
                  <div style={{ padding: isMobile ? '12px' : '15px', backgroundColor: '#f8fafc', borderRadius: isMobile ? '8px' : '10px', border: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>
                    No past sales found for this property.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {pastSales.map((sale, index) => (
                      <div
                        key={`${sale.transactionId || 'sale'}-${index}`}
                        style={{
                          padding: isMobile ? '12px' : '15px',
                          backgroundColor: '#fff7ed',
                          borderRadius: isMobile ? '8px' : '10px',
                          border: '1px solid #fdba74'
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '160px 160px 1fr', gap: '10px' }}>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Sale date</div>
                            <div style={{ color: '#7c2d12', fontWeight: '600' }}>{formatDisplayDate(sale.saleDate)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Price</div>
                            <div style={{ color: '#7c2d12', fontWeight: '600' }}>{formatDisplayPrice(sale.price)}</div>
                          </div>
                          <div>
                            <div style={{ color: '#9a3412', fontSize: '12px', marginBottom: '4px', fontWeight: '600' }}>Included parcels</div>
                            <div style={{ color: '#7c2d12', whiteSpace: 'pre-wrap' }}>
                              {Array.isArray(sale.includedParcels) && sale.includedParcels.length > 0
                                ? sale.includedParcels.join(' • ')
                                : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Owners Section */}
            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h3 style={{
                color: '#1e293b',
                marginBottom: isMobile ? '10px' : '15px',
                fontSize: isMobile ? '16px' : '18px'
              }}>Owners</h3>
              {selectedLead.owners && selectedLead.owners.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedLead.owners.map((owner, idx) => (
                    <div key={idx} style={{
                      padding: isMobile ? '12px' : '15px',
                      backgroundColor: '#f8fafc',
                      borderRadius: isMobile ? '8px' : '10px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        marginBottom: '10px',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '10px' : '0'
                      }}>
                        <div>
                          <div style={{ color: '#1e293b', fontWeight: '600', fontSize: '15px' }}>{owner.name}</div>
                          <div style={{ color: '#64748b', fontSize: '13px', marginTop: '4px' }}>Share: {owner.share} • Type: {owner.type}</div>
                          {owner.dateOfBirth && <div style={{ color: '#64748b', fontSize: '13px', marginTop: '2px' }}>Born: {owner.dateOfBirth}</div>}
                          {owner.address && <div style={{ color: '#64748b', fontSize: '13px', marginTop: '2px' }}>Address: {owner.address}</div>}
                          {owner.emso && <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>EMSO: {owner.emso}</div>}
                          {(() => {
                            const otherProperties = ownerOtherPropertiesMap[normalizeOwnerName(owner.name)] || [];
                            if (otherProperties.length === 0) return null;
                            return (
                              <div style={{ color: '#0f172a', fontSize: '12px', marginTop: '6px' }}>
                                Other properties:{' '}
                                {otherProperties.map((lead, index) => (
                                  <span key={`${lead.id}-${index}`}>
                                    {lead.katastrskaObcina} {lead.idParceleStavbe}{lead.type ? ` (${lead.type})` : ''}
                                    {index < otherProperties.length - 1 ? ' • ' : ''}
                                  </span>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {owner.contactId && (
                            <button
                              onClick={() => navigate(`/real-estate/contacts?contactId=${owner.contactId}`)}
                              title="Open linked contact"
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <UserRound size={14} />
                              Contact
                            </button>
                          )}
                          <button
                            onClick={() => {
                              const formattedAddress = owner.address ? owner.address.replace(/,\s*/g, '\n') : 'NASLOV\nKRAJ';
                              const shouldHideLocation = isHouseType(selectedLead.type) && String(selectedLead.naslov || '').trim();

                              setLetterOptions({
                                owner,
                                location: shouldHideLocation ? '' : (selectedLead.koName || ''),
                                gender: 'male',
                                address: formattedAddress,
                                parcelCount: 1,
                                extraParcel2: '',
                                extraParcel3: ''
                              });
                              setShowLetterPopup(true);
                            }}
                            title="Generate inquiry letter for this owner"
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#0891b2',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <FileText size={14} />
                            Letter
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '10px', marginTop: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Phone</label>
                          <input
                            type="tel"
                            value={owner.phone || ''}
                            onChange={(e) => {
                              const updated = { ...selectedLead };
                              updated.owners[idx].phone = e.target.value;
                              setSelectedLead(updated);
                            }}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                            placeholder="+386 XX XXX XXX"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Email</label>
                          <input
                            type="email"
                            value={owner.email || ''}
                            onChange={(e) => {
                              const updated = { ...selectedLead };
                              updated.owners[idx].email = e.target.value;
                              setSelectedLead(updated);
                            }}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                            placeholder="email@example.com"
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '5px' }}>Notes</label>
                          <textarea
                            value={owner.notes || ''}
                            onChange={(e) => {
                              const updated = { ...selectedLead };
                              updated.owners[idx].notes = e.target.value;
                              setSelectedLead(updated);
                            }}
                            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', minHeight: '100px', resize: 'vertical', boxSizing: 'border-box' }}
                            placeholder="Add notes about this owner..."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>
                  No owner information available
                </div>
              )}
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: isMobile ? '10px' : '15px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '10px' : '0'
              }}>
                <h3 style={{
                  color: '#1e293b',
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px',
                  width: isMobile ? '100%' : 'auto'
                }}>Interactions & Calls</h3>
              </div>

              <div style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      color: '#64748b',
                      marginBottom: '6px',
                      fontWeight: '500'
                    }}>
                      Type of Interaction
                    </label>
                    <select
                      value={newInteractionType}
                      onChange={(e) => setNewInteractionType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="Call">Call</option>
                      <option value="In Person Talk">In Person Talk</option>
                      <option value="Email">Email</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      color: '#64748b',
                      marginBottom: '6px',
                      fontWeight: '500'
                    }}>
                      Notes
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={newInteractionNotes}
                        onChange={(e) => setNewInteractionNotes(e.target.value)}
                        onKeyPress={async (e) => {
                          if (e.key === 'Enter' && newInteractionNotes.trim()) {
                            try {
                              const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/tickets`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({
                                  ticketType: 'interaction',
                                  noteContent: `${newInteractionType}: ${newInteractionNotes}`,
                                  userName: localStorage.getItem('leadGenUser') || 'User'
                                })
                              });
                              if (response.ok) {
                                setNewInteractionNotes('');
                                await fetchTickets(selectedLead.id);
                              }
                            } catch (error) {
                              console.error('Error adding interaction:', error);
                            }
                          }
                        }}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          fontSize: '14px',
                          outline: 'none'
                        }}
                        placeholder="Describe the interaction..."
                      />
                      <button
                        onClick={async () => {
                          if (!newInteractionNotes.trim()) return;
                          try {
                            const response = await authFetch(`${API_BASE}/api/leads/properties/${selectedLead.id}/tickets`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                ticketType: 'interaction',
                                noteContent: `${newInteractionType}: ${newInteractionNotes}`,
                                userName: localStorage.getItem('leadGenUser') || 'User'
                              })
                            });
                            if (response.ok) {
                              setNewInteractionNotes('');
                              await fetchTickets(selectedLead.id);
                            }
                          } catch (error) {
                            console.error('Error adding interaction:', error);
                            alert('Failed to add interaction.');
                          }
                        }}
                        disabled={!newInteractionNotes.trim()}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: newInteractionNotes.trim() ? '#0891b2' : '#e2e8f0',
                          color: newInteractionNotes.trim() ? 'white' : '#94a3b8',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: newInteractionNotes.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Plus size={16} />
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '10px' : '15px', flexWrap: 'wrap' }}>
                <h3 style={{
                  color: '#1e293b',
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px'
                }}>General Notes</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!hasPropertyChecklistSection ? (
                    <button
                      onClick={() => {
                        setHasAddedPropertyChecklistSection(true);
                        setShowChecklistModal(true);
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#fff7ed',
                        color: '#9a3412',
                        border: '1px solid #fdba74',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <CheckSquare size={15} /> Checklist
                    </button>
                  ) : null}
                  {isUnbuiltLandType(selectedLead.type) && !hasLeadMptChecklistSection ? (
                    <button
                      type="button"
                      onClick={() => setHasAddedLeadMptChecklist(true)}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#ecfdf5',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <CheckSquare size={15} /> Add MPT Checklist
                    </button>
                  ) : null}
                </div>
              </div>
              <textarea
                value={selectedLead.notes || ''}
                onChange={(e) => {
                  const updated = { ...selectedLead };
                  updated.notes = e.target.value;
                  setSelectedLead(updated);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 500) + 'px';
                }}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 500) + 'px';
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '150px',
                  resize: 'none',
                  overflow: 'hidden',
                  boxSizing: 'border-box'
                }}
                placeholder="Add general notes about this property lead..."
              />
            </div>

            {hasLeadMptChecklistSection ? (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <MasterParcelChecklistSection
                  entityId={selectedLead.id}
                  entityType={CHECKLIST_ENTITY_TYPE}
                  scope="lead_gen_property_mpt"
                  propertyForm={selectedLead}
                  title="MPT Checklist"
                  defaultExpanded={false}
                />
              </div>
            ) : null}

            {hasPropertyChecklistSection && (
              <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '10px' : '15px', flexWrap: 'wrap' }}>
                  <h3 style={{ color: '#1e293b', margin: 0, fontSize: isMobile ? '16px' : '18px' }}>Checklist</h3>
                  <button
                    type="button"
                    onClick={() => setShowChecklistModal(true)}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#fff7ed',
                      color: '#9a3412',
                      border: '1px solid #fdba74',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <CheckSquare size={15} /> Edit
                  </button>
                </div>
                {filledChecklistItems.length > 0 ? (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {filledChecklistItems.map(({ item, value }) => (
                      <div key={item.id} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                        <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                        {item.type === 'checkbox' ? (
                          <div style={{ color: '#16a34a', fontSize: '18px', fontWeight: '700', lineHeight: 1 }}>✓</div>
                        ) : item.type === 'document' ? (
                          <div style={{ display: 'grid', gap: '8px' }}>
                            {getChecklistDocumentValues(value).map((documentValue, index) => (
                              <button key={`${documentValue.documentPath}-${index}`} onClick={() => handleDocumentDownload(documentValue.documentPath, documentValue.documentName)} style={{ padding: '6px 12px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>
                                <span>⬇</span>
                                {documentValue.documentName || 'Download'}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div style={{ color: '#1e293b', fontWeight: '500', whiteSpace: 'pre-wrap' }}>{formatChecklistValue(item, value)}</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>No checklist items filled yet.</div>
                )}
              </div>
            )}

            <div style={{ marginBottom: isMobile ? '15px' : '25px' }}>
              <h3 style={{
                color: '#1e293b',
                marginBottom: isMobile ? '10px' : '15px',
                fontSize: isMobile ? '16px' : '18px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Activity History (Tickets)
              </h3>

              <div style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Add Note
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newTicketNote}
                    onChange={(e) => setNewTicketNote(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNoteTicket()}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    placeholder="Type a note and press Enter or click Add..."
                  />
                  <button
                    onClick={handleAddNoteTicket}
                    disabled={!newTicketNote.trim()}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: newTicketNote.trim() ? '#3b82f6' : '#e2e8f0',
                      color: newTicketNote.trim() ? 'white' : '#94a3b8',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: newTicketNote.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Plus size={16} />
                    Add
                  </button>
                </div>
              </div>

              <div style={{
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: '#faf5ff',
                borderRadius: '8px',
                border: '1px solid #e9d5ff'
              }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  color: '#64748b',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Upload Document
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    disabled={uploadingDocument}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
                  />
                  {uploadingDocument && (
                    <span style={{ color: '#8b5cf6', fontSize: '13px' }}>Uploading...</span>
                  )}
                </div>
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
                  Allowed: PDF, DOC, DOCX, XLS, XLSX, Images, ZIP, RAR (max 10MB)
                </p>
              </div>

              <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {tickets.length > 0 ? (
                  tickets.map((ticket) => {
                    const formatted = formatTicket(ticket);
                    return (
                      <div key={ticket.id} style={{
                        padding: '12px 15px',
                        backgroundColor: 'white',
                        border: `1px solid ${formatted.color}30`,
                        borderLeft: `4px solid ${formatted.color}`,
                        borderRadius: '8px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span style={{ fontSize: '18px' }}>{formatted.icon}</span>
                            <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                              {formatted.title}
                            </span>
                          </div>
                          <span style={{ color: '#94a3b8', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {new Date(ticket.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ color: '#475569', fontSize: '13px', marginLeft: '26px' }}>
                          {formatted.content}
                        </div>
                        {ticket.ticketType === 'document' && ticket.documentName && (
                          <button
                            onClick={() => handleDocumentDownload(ticket.documentPath, ticket.documentName)}
                            style={{
                              marginLeft: '26px',
                              marginTop: '8px',
                              padding: '6px 12px',
                              backgroundColor: '#8b5cf6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <span>⬇</span>
                            Download
                          </button>
                        )}
                        {ticket.userName && (
                          <div style={{ color: '#94a3b8', fontSize: '11px', marginLeft: '26px', marginTop: '4px' }}>
                            by {ticket.userName}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px dashed #e2e8f0'
                  }}>
                    No activity history yet. Add a note above or make changes to start tracking.
                  </div>
                )}
              </div>
            </div>

            <div style={{
              marginTop: isMobile ? '15px' : '25px',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setTickets([]);
                  setNewTicketNote('');
                  setNewInteractionNotes('');
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Cancel
              </button>
              <button
                onClick={exportPropertyDetailsToExcel}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center'
                }}
              >
                <Download size={16} />
                Export Excel
              </button>
              <button
                onClick={exportPropertyDetailsToPDF}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#0891b2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center'
                }}
              >
                <Download size={16} />
                Export PDF
              </button>
              <button
                onClick={async () => {
                  try {
                    await persistPropertyLead(selectedLead);
                    await fetchPropertyLeads();
                    await fetchTickets(selectedLead.id);
                    alert('Changes saved successfully!');
                  } catch (error) {
                    console.error('Error saving:', error);
                    alert('Error saving changes: ' + error.message);
                  }
                }}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#d97706',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Save Changes
              </button>
            </div>

            <div id="property-detail-print" style={{ display: 'none' }}>
              <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
                <h1 style={{ fontSize: '24px', marginBottom: '20px' }}>Property Details</h1>

                <h2 style={{ fontSize: '16px', marginBottom: '10px' }}>Property Information</h2>
                <table style={{ width: '100%', marginBottom: '20px', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold', width: '30%' }}>Katastrska občina:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.katastrskaObcina} {selectedLead?.koName && `(${selectedLead.koName})`}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>ID parcele/stavbe:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.idParceleStavbe}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Type:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.type}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Status:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.status}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>Owner Names:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.ownerNames}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold' }}>ZK Date:</td>
                      <td style={{ padding: '8px' }}>{selectedLead?.zkDate}</td>
                    </tr>
                  </tbody>
                </table>

                {selectedLead?.owners && selectedLead?.owners.length > 0 && (
                  <>
                    <h2 style={{ fontSize: '16px', marginBottom: '10px' }}>Owners</h2>
                    <table style={{ width: '100%', marginBottom: '20px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f0f0f0', borderBottom: '2px solid #ddd' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Name</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Share</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Phone</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLead?.owners.map((owner, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px' }}>{owner.name}</td>
                            <td style={{ padding: '8px' }}>{owner.share}</td>
                            <td style={{ padding: '8px' }}>{owner.type}</td>
                            <td style={{ padding: '8px' }}>{owner.phone || '-'}</td>
                            <td style={{ padding: '8px' }}>{owner.address || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {selectedLead?.notes && (
                  <>
                    <h2 style={{ fontSize: '16px', marginBottom: '10px' }}>Notes</h2>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{selectedLead.notes}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Letter Customization Popup */}
      {showLetterPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '25px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ color: '#1e293b', marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>Customize Letter</h3>
            
            {/* Gender Selection */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                Salutation:
              </label>
              <select 
                value={letterOptions.gender} 
                onChange={(e) => setLetterOptions({...letterOptions, gender: e.target.value})}
                style={inputStyle}
              >
                <option value="male">Male (Spoštovani g.)</option>
                <option value="female">Female (Spoštovana ga.)</option>
              </select>
            </div>

            {!(isHouseType(selectedLead?.type) && String(selectedLead?.naslov || '').trim()) && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                  Location (KRAJ):
                </label>
                <input 
                  type="text" 
                  value={letterOptions.location} 
                  onChange={(e) => setLetterOptions({...letterOptions, location: e.target.value})}
                  style={inputStyle}
                />
              </div>
            )}

            {/* Address Editor */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                Address (Edit Layout):
              </label>
              <textarea 
                value={letterOptions.address} 
                onChange={(e) => setLetterOptions({...letterOptions, address: e.target.value})}
                style={{...inputStyle, minHeight: '80px', fontFamily: 'monospace', resize: 'vertical'}}
              />
            </div>

            {/* Number of Parcels */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                Number of parcels:
              </label>
              <select
                value={letterOptions.parcelCount}
                onChange={(e) => setLetterOptions({...letterOptions, parcelCount: Number(e.target.value)})}
                style={inputStyle}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </div>

            {letterOptions.parcelCount >= 2 && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                  2nd parcel (e.g. 123/4):
                </label>
                <input
                  type="text"
                  value={letterOptions.extraParcel2}
                  onChange={(e) => setLetterOptions({...letterOptions, extraParcel2: e.target.value})}
                  placeholder="e.g. 123/4"
                  style={inputStyle}
                />
              </div>
            )}

            {letterOptions.parcelCount >= 3 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: '#64748b' }}>
                  3rd parcel (e.g. 567/8):
                </label>
                <input
                  type="text"
                  value={letterOptions.extraParcel3}
                  onChange={(e) => setLetterOptions({...letterOptions, extraParcel3: e.target.value})}
                  placeholder="e.g. 567/8"
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={() => {
                  generatePropertyInquiryLetter(letterOptions.owner, selectedLead, letterOptions);
                  setShowLetterPopup(false);
                }}
                style={{ 
                  flex: 1, 
                  padding: '10px', 
                  backgroundColor: '#0891b2', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Download PDF
              </button>
              <button 
                onClick={() => setShowLetterPopup(false)}
                style={{ 
                  flex: 1, 
                  padding: '10px', 
                  backgroundColor: '#f1f5f9', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const tableHeaderStyle = {
  padding: '16px 20px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: '600',
  color: '#475569',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tableCellStyle = {
  padding: '16px 20px',
  fontSize: '14px'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

export default LeadGen;
